/**
 * Parent-side image and SVG editor. Handles click-to-swap for images
 * (via file picker + AssetStore) and SVGs (via external SVG library browser).
 * Communicates with iframe via bridge postMessage.
 *
 * Image state prefers `pb-asset://{assetId}` URIs, but can also store direct
 * external URLs when remote hosts block browser-side asset download by CORS.
 */

import { store } from '../core/state.js';
import { eventBus } from '../core/events.js';
import { bridge } from './bridge.js';
import {
  escapeForAttrSelector,
  findEditableByEid,
  findEditableByIndex,
  injectEditableAttrs,
  stripTempAttrs
} from './editable-utils.js';
import { resolveSectionByInstance } from './section-utils.js';
import { buildImageSlots } from './image-slot-queue.js';
import { formatSectionName } from '../shared/section-display-name.js';
import { getAssetBase, loadJSON } from '../core/asset-loader.js';
import { hasPanelFor } from './glass-panels.js';
import { showUndoToast } from '../ui/shell.js';
import { escapeHTML } from '../shared/html-escape.js';
import { createModal, createButton, createSegmentedControl } from '../ui/components.js';
import { openCollectionBrowser } from '../ui/collection-browser.js';
import { createThumbnailPool } from '../ui/collection-thumbnails.js';
import { pixabayVectors } from './adapters/pixabay.js';
import { showImagePicker, prepareUploadFile } from './image-picker.js';
import { installDropTarget, createFileInput } from './image-picker/upload.js';
import {
  replaceSectionCssBackgroundImage,
  clearSectionCssBackgroundImage
} from '../shared/css-background-images.js';
import {
  listDecorativeSvgs,
  replaceDecorativeSvg,
  removeDecorativeSvg,
  removeDecorativeByEid,
  serializeDecorativePreview
} from './insert-decorative-svg.js';
import { filterSvgLibraryEntries, getSvgAssetUrl } from './svg-library-utils.js';
import { parseSvg, sanitizeSvg, serializeSvg } from './svg-sanitizer.js';
import { reportError } from '../core/report-error.js';
import { domTargetRef, isDomTarget, targetRefFor } from '../shared/target-ref.js';
import { classifyElementKind, kindLabel } from '../shared/element-kind.js';
import {
  openReplaceDialog,
  snapshotReplacementTarget,
  validateReplacementTarget
} from './replace-dispatcher.js';

let svgIndex = null;
let svgModal = null;
let svgModalKit = null;
let svgModalTrigger = null;
let svgModalRestoreFocusOnClose = true;
let svgModalRequestToken = 0;
let svgModalOutcome = null;
let svgPixabayAbort = null;
let svgPixabayGeneration = 0;
let decorativeSvgIndex = null;
let activeImageEdit = null;
let activeSvgEdit = null;
let svgImageInlineToken = 0;

const PICTURE_SCOPE_ALL = 'all';
const PICTURE_SCOPE_DESKTOP = 'desktop';
const PICTURE_SCOPE_MOBILE = 'mobile';

function beginSvgModalOutcome() {
  settleSvgModalOutcome();
  let resolve;
  const promise = new Promise((done) => {
    resolve = done;
  });
  svgModalOutcome = { promise, resolve, picked: false };
  return promise;
}

function markSvgModalPicked() {
  if (svgModalOutcome) svgModalOutcome.picked = true;
}

function settleSvgModalOutcome() {
  const outcome = svgModalOutcome;
  if (!outcome) return;
  svgModalOutcome = null;
  outcome.resolve({ status: outcome.picked ? 'picked' : 'cancelled' });
}

/** Resolve only when the currently open SVG browser is picked or dismissed. */
export function waitForSvgModalOutcome() {
  return svgModalOutcome?.promise || Promise.resolve({ status: 'cancelled' });
}

function retireMediaTargets({ instanceIds = [] } = {}) {
  const affected = new Set(instanceIds);
  if (!affected.has(activeImageEdit?.instanceId) && !affected.has(activeSvgEdit?.instanceId))
    return;
  svgImageInlineToken += 1;
  if (affected.has(activeImageEdit?.instanceId)) activeImageEdit = null;
  if (affected.has(activeSvgEdit?.instanceId)) activeSvgEdit = null;
  closeSvgModal();
}

export function initImageEditor() {
  eventBus.on('editable-ids:repaired', retireMediaTargets);
  eventBus.on('iframe:element-clicked', onElementClicked);
  eventBus.on('iframe:element-primary-action', onElementPrimaryAction);
  // Pre-Publish Audit v1.1 — clicking "Fix" on a template-hotlink-assets
  // finding routes here. We construct an explicit target from the finding's
  // location/payload and hand it to the existing replaceImage(target) entry
  // point so the audit's Fix button actually opens the image picker.
  eventBus.on('editing:request-image-replace', onAuditImageReplaceRequest);
  // Canvas drag-drop of an image file / Cmd+V of a screenshot over an image.
  eventBus.on('iframe:image-file-dropped', onImageFileDropped);
}

export function destroyImageEditor() {
  eventBus.off('editable-ids:repaired', retireMediaTargets);
  svgImageInlineToken += 1;
  activeImageEdit = null;
  activeSvgEdit = null;
  eventBus.off('iframe:element-clicked', onElementClicked);
  eventBus.off('iframe:element-primary-action', onElementPrimaryAction);
  eventBus.off('editing:request-image-replace', onAuditImageReplaceRequest);
  eventBus.off('iframe:image-file-dropped', onImageFileDropped);
  closeSvgModal();
}

/**
 * Replace an editable image (or CSS background) with a file dropped onto the
 * canvas or pasted over it. Runs the same guardrails + art-direction scope
 * flow as the picker's upload path.
 */
async function onImageFileDropped(data) {
  const file = data && data.droppedFile;
  if (!file || typeof file !== 'object' || typeof file.type !== 'string') return;
  if (!data.instanceId) return;
  const dropValidity = validateReplacementTarget(data, { mimeType: file.type });
  if (!dropValidity.ok) {
    eventBus.emit('editing:error', { message: `Replacement skipped: ${dropValidity.reason}` });
    return;
  }

  const captured = snapshotReplacementTarget(data);
  if (!captured.ok) return;
  data = captured.target;

  if (data.kind === 'logo') {
    const logo = await import('./logo-editor.js');
    if (!validateReplacementTarget(data).ok) return;
    logo.setActiveLogoEdit(data);
    await logo.applyLogoFile({ file, meta: { source: 'Drop or paste' } });
    return;
  }

  let prepared;
  try {
    prepared = await prepareUploadFile(file);
  } catch (err) {
    eventBus.emit('editing:error', { message: err?.message || 'Could not use that image.' });
    return;
  }

  if (!validateReplacementTarget(data).ok) return;
  const edit = {
    ...data,
    instanceId: data.instanceId,
    editableIndex: data.editableIndex,
    eid: data.eid,
    editableType: data.editableType === 'css-background' ? 'css-background' : data.editableType,
    cssBgIndex: data.cssBgIndex,
    targetRef: data.targetRef,
    sectionId: data.sectionId
  };

  if (edit.editableType === 'svg') {
    const owner = { ...data };
    activeSvgEdit = owner;
    activeImageEdit = null;
    if (prepared.type === 'image/svg+xml') {
      const markup = await readFileAsText(prepared);
      if (!markup || !validateReplacementTarget(edit).ok) return;
      if (activeSvgEdit !== owner) return;
      replaceSvg(markup, { preserveTargetClass: true, preserveTargetStyle: true });
    } else {
      await applySvgToImageFile(prepared, { source: 'Drop or paste' });
      eventBus.emit('editing:disclosure', { message: 'SVG swapped to a raster image.' });
    }
    return;
  }

  edit.editableType = edit.editableType === 'css-background' ? 'css-background' : 'image';
  let scope = PICTURE_SCOPE_ALL;
  if (edit.editableType === 'image') {
    scope = await choosePictureReplaceScope(edit);
    if (!scope || !validateReplacementTarget(edit).ok) return; // cancelled or retired
  }

  svgImageInlineToken += 1;
  activeImageEdit = { ...edit, pictureReplaceScope: scope };
  activeSvgEdit = null;
  await applyImageFile(prepared, { source: 'Upload' }, activeImageEdit);
}

function onElementPrimaryAction(data) {
  if (!data || !/^(?:photo|logo|icon|decoration|background)$/.test(data.kind || '')) return;
  import('./replace-dispatcher.js')
    .then(({ openReplaceDialog }) => openReplaceDialog({ target: data }))
    .catch((err) => reportError('replace-primary-action', err, { user: 'toast' }));
}

function onAuditImageReplaceRequest({ finding } = {}) {
  if (!finding) return;
  const loc = finding.location || {};
  const payload = finding.fix?.payload || {};
  const eid = loc.eid || payload.eid || null;
  const target = {
    instanceId: loc.instanceId || payload.instanceId || null,
    eid,
    targetRef: domTargetRef(eid),
    editableType: payload.editableType || 'image',
    kind: payload.kind || 'photo',
    currentUrl: payload.currentUrl || '',
    initialTab: payload.initialTab || '',
    initialCategory: payload.initialCategory || payload.role || ''
  };
  if (!target.instanceId || !target.eid) return;

  openReplaceDialog({ target }).catch((err) => {
    console.warn('[ImageEditor] audit-driven image replace failed:', err?.message || err);
  });
}

// resolveSectionByInstance moved to section-utils.js

function onElementClicked(data) {
  if (data.editableType === 'image') {
    if (!isDomTarget(data)) return;
    const clickToken = ++svgImageInlineToken;
    activeImageEdit = data;
    activeSvgEdit = null;
    if (shouldRouteImageClickToSvgPanel(data)) {
      inlineActiveSvgImage(data, clickToken).then((didInline) => {
        if (didInline) return;
        if (clickToken !== svgImageInlineToken) return;
        if (hasPanelFor('image')) return;
        eventBus.emit('editing:image-selected', data);
      });
      return;
    }
    if (hasPanelFor('image')) return;
    eventBus.emit('editing:image-selected', data);
  } else if (data.editableType === 'css-background') {
    svgImageInlineToken += 1;
    activeImageEdit = data;
    activeSvgEdit = null;
    eventBus.emit('editing:css-background-selected', data);
  } else if (data.editableType === 'svg') {
    if (!isDomTarget(data)) return;
    svgImageInlineToken += 1;
    activeSvgEdit = data;
    activeImageEdit = null;
    eventBus.emit('editing:svg-selected', data);
  }
}

function shouldRouteImageClickToSvgPanel(data) {
  const target = getEditableElementForEdit(data);
  return isDecorativeSvgImage(target, data?.kind);
}

function getEditableElementForEdit(data) {
  if (!data || !data.instanceId) return null;
  const resolved = resolveSectionByInstance(data.instanceId);
  if (!resolved?.section?.html) return null;
  if (data.eid) {
    const byEid = findEditableByEid(resolved.section.html, data.eid);
    if (byEid) return byEid;
  }
  return findEditableByIndex(resolved.section.html, data.editableIndex);
}

function isSvgUrl(value) {
  const raw = String(value || '').trim();
  if (!raw || raw.startsWith('data:')) return false;
  return /\.svg(?:[?#].*)?$/i.test(raw);
}

function shouldSkipCrossOriginSvgFetch(value) {
  if (typeof window === 'undefined' || !window.location?.href) return false;
  try {
    const parsed = new URL(value, window.location.href);
    if (!/^https?:$/i.test(parsed.protocol)) return false;
    const current = new URL(window.location.href);
    if (parsed.origin === current.origin) return false;
    const assetBase = getAssetBase();
    if (assetBase && parsed.href.startsWith(`${assetBase}/`)) return false;
    return true;
  } catch {
    return false;
  }
}

function isDecorativeSvgImage(el, knownKind = '') {
  if (!el || String(el.tagName || '').toUpperCase() !== 'IMG') return false;
  const src = el.getAttribute('src') || el.getAttribute('data-src') || '';
  if (!isSvgUrl(src)) return false;
  return (knownKind || classifyElementKind(el, { editableType: 'image' })) === 'decoration';
}

function sanitizeSvgMarkupForInline(svgMarkup, sourceImg = null) {
  // Use the canonical sanitizer (svg-sanitizer.js) rather than a divergent local
  // copy; then re-apply this path's source-image attribute carry-over.
  const svg = parseSvg(svgMarkup);
  if (!svg) return '';
  sanitizeSvg(svg);

  if (sourceImg) {
    const sourceClasses = String(sourceImg.getAttribute('class') || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (sourceClasses.length) {
      const svgClasses = new Set(
        String(svg.getAttribute('class') || '')
          .trim()
          .split(/\s+/)
          .filter(Boolean)
      );
      for (const cls of sourceClasses) svgClasses.add(cls);
      svg.setAttribute('class', Array.from(svgClasses).join(' '));
    }
    for (const attr of ['width', 'height', 'aria-hidden', 'role']) {
      const value = sourceImg.getAttribute(attr);
      if (value && !svg.hasAttribute(attr)) svg.setAttribute(attr, value);
    }
    if (sourceImg.getAttribute('aria-hidden') === 'true' && !svg.hasAttribute('focusable')) {
      svg.setAttribute('focusable', 'false');
    }
  }

  return serializeSvg(svg);
}

async function inlineActiveSvgImage(data, token) {
  const captured = snapshotReplacementTarget(data);
  if (!captured.ok) return true;
  const target = getEditableElementForEdit(data);
  if (!isDecorativeSvgImage(target)) return false;

  const src = target.getAttribute('src') || target.getAttribute('data-src') || '';
  let url = src;
  try {
    url = new URL(src, window.location.href).href;
  } catch (_) {
    // Keep the original src when URL parsing is unavailable.
  }

  if (shouldSkipCrossOriginSvgFetch(url)) return false;

  try {
    const response = await fetch(url, { credentials: 'omit' });
    if (token !== svgImageInlineToken) return true;
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const svgText = await response.text();
    if (token !== svgImageInlineToken) return true;
    const inlineSvg = sanitizeSvgMarkupForInline(svgText, target);
    if (!inlineSvg) throw new Error('Invalid SVG markup');
    if (activeImageEdit !== data || !validateReplacementTarget(captured.target).ok) return true;
    swapImageToSvg(inlineSvg);
    return true;
  } catch (err) {
    console.warn('[ImageEditor] Could not inline decorative SVG image for editing:', err);
    return false;
  }
}

/**
 * Set the active SVG edit target programmatically (e.g. from the section list SVG panel).
 * Must be called before openSvgLibrary() so the library knows which SVG to replace.
 * @param {{ instanceId: string, editableIndex: number, editableType: string }} data
 */
export function setActiveSvgEdit(data) {
  activeSvgEdit = data
    ? {
        ...data,
        kind: data.kind || 'icon',
        targetRef: targetRefFor(data)
      }
    : null;
  activeImageEdit = null;
}

/**
 * Set the active image edit target programmatically (e.g. from section list image panel).
 */
export function setActiveImageEdit(data) {
  activeImageEdit = data
    ? {
        ...data,
        kind: data.kind || (data.editableType === 'css-background' ? 'background' : 'photo'),
        targetRef: targetRefFor(data)
      }
    : null;
  activeSvgEdit = null;
}

function normalizePictureReplaceScope(scope) {
  const value = String(scope || '')
    .trim()
    .toLowerCase();
  if (value === PICTURE_SCOPE_DESKTOP || value === PICTURE_SCOPE_MOBILE) return value;
  return PICTURE_SCOPE_ALL;
}

function mediaLooksMobile(media) {
  return /\bmax-width\b/i.test(String(media || ''));
}

function mediaLooksDesktop(media) {
  const raw = String(media || '').trim();
  if (!raw) return true;
  if (/\bmin-width\b/i.test(raw)) return true;
  return !mediaLooksMobile(raw);
}

function pictureHasArtDirection(picture) {
  if (!picture) return false;
  return Array.from(picture.querySelectorAll('source')).some((source) => {
    const media = source.getAttribute('media') || '';
    return /\b(?:max|min)-width\b/i.test(media);
  });
}

function getPictureForEdit(edit) {
  const target = getEditableElementForEdit(edit);
  if (!target || String(target.tagName || '').toUpperCase() !== 'IMG') return null;
  return target.closest('picture');
}

function choosePictureReplaceScope(edit) {
  const picture = getPictureForEdit(edit);
  if (!pictureHasArtDirection(picture)) return Promise.resolve(PICTURE_SCOPE_ALL);

  return new Promise((resolve) => {
    let settled = false;
    const settle = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    const { body, footer, close } = createModal({
      title: 'Replace Responsive Image',
      size: null,
      overlayClassName: 'pb-confirm-overlay',
      onClose: () => settle(null)
    });

    const msg = document.createElement('p');
    msg.className = 'pb-confirm-msg';
    msg.textContent = 'This image has separate mobile and desktop sources.';
    body.appendChild(msg);

    const detail = document.createElement('p');
    detail.className = 'pb-confirm-detail';
    detail.textContent =
      'Choose which source set to replace. Existing breakpoint-specific sources stay intact unless selected here.';
    body.appendChild(detail);

    footer.classList.add('pb-confirm-footer');
    const choices = [
      { value: PICTURE_SCOPE_MOBILE, label: 'Mobile' },
      { value: PICTURE_SCOPE_DESKTOP, label: 'Desktop' },
      { value: PICTURE_SCOPE_ALL, label: 'Both' }
    ];
    for (const choice of choices) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className =
        choice.value === PICTURE_SCOPE_ALL ? 'pb-btn pb-btn-primary' : 'pb-btn pb-btn-secondary';
      btn.textContent = choice.label;
      btn.addEventListener('click', () => {
        settle(choice.value);
        close();
      });
      footer.appendChild(btn);
    }
  });
}

function buildImageAttribution(meta = null) {
  const source = String(meta?.source || '').trim();
  if (!/unsplash/i.test(source)) return null;
  const author = String(meta?.author || '').trim();
  if (!author) return null;
  return {
    source: 'Unsplash',
    author,
    authorUrl: String(meta?.authorUrl || '').trim()
  };
}

function findCreditAnchor(target) {
  if (!target) return null;
  const media = target.closest('picture') || target;
  const parentLink = media.parentElement?.matches?.('a') ? media.parentElement : null;
  return parentLink || media;
}

function removeAdjacentImageCredit(anchor) {
  if (!anchor) return;
  let next = anchor.nextElementSibling;
  while (next && next.matches('[data-pb-image-credit]')) {
    const current = next;
    next = next.nextElementSibling;
    current.remove();
  }
}

function applyAdjacentImageCredit(doc, target, attribution, assetId = '') {
  const anchor = findCreditAnchor(target);
  if (!anchor) return;
  removeAdjacentImageCredit(anchor);
  if (!attribution) return;

  const credit = doc.createElement('span');
  credit.setAttribute('data-pb-image-credit', 'true');
  if (assetId) credit.setAttribute('data-pb-credit-for', assetId);
  credit.className = 'pb-image-credit';
  credit.setAttribute(
    'style',
    'display:block;font-size:.75rem;line-height:1.35;margin-top:.35rem;color:currentColor;opacity:.76'
  );

  credit.appendChild(doc.createTextNode('Photo by '));
  if (attribution.authorUrl) {
    const link = doc.createElement('a');
    link.href = attribution.authorUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = attribution.author;
    credit.appendChild(link);
  } else {
    credit.appendChild(doc.createTextNode(attribution.author));
  }
  credit.appendChild(doc.createTextNode(' on Unsplash'));

  anchor.after(credit);
}

/**
 * Store file as asset, update active image's HTML, send preview to iframe.
 * Shared helper — consolidates logic from replaceImage, replaceImageFromPexels, etc.
 * @param {File} file
 * @param {{ source?: string, author?: string, authorUrl?: string }} [meta]
 */
async function applyImageFile(file, meta, target = null) {
  const captured = snapshotReplacementTarget(target || activeImageEdit);
  if (!captured.ok) return false;
  const edit = captured.target;

  const assetId = await store.assets.store(file, meta);
  const assetUri = `pb-asset://${assetId}`;
  const validity = validateReplacementTarget(edit, { mimeType: file.type || '' });
  if (!validity.ok) {
    eventBus.emit('editing:error', { message: `Replacement skipped: ${validity.reason}` });
    return false;
  }
  let resolved = validity.resolved;
  if (!resolved) return false;

  if (edit.editableType === 'css-background') {
    const updatedCss = replaceSectionCssBackgroundImage(
      resolved.section.css,
      resolved.section.sectionId,
      edit.targetRef || edit.cssBgIndex,
      assetUri
    );
    if (updatedCss === null) return false;

    store.update(`${resolved.sectionPath}.css`, updatedCss, 'Replace background image');
    eventBus.emit('preview:render-request', { preserveScroll: true });
    eventBus.emit('editing:image-replaced', {
      instanceId: edit.instanceId,
      assetId,
      filename: file.name,
      kind: edit.kind || 'background'
    });
    return true;
  }

  let previewUrl = '';
  try {
    previewUrl = await fileToDataUrl(file);
  } catch {
    previewUrl = await store.assets.getBlobUrl(assetId);
  }

  const finalValidity = validateReplacementTarget(edit, { mimeType: file.type || '' });
  if (!finalValidity.ok) return false;
  resolved = finalValidity.resolved;

  if (previewUrl) {
    bridge.send('update-image', {
      instanceId: edit.instanceId,
      editableIndex: edit.editableIndex,
      eid: edit.eid || edit.targetRef?.eid || '',
      src: previewUrl,
      alt: '',
      assetId,
      mimeType: file.type || '',
      pictureReplaceScope: edit.pictureReplaceScope || PICTURE_SCOPE_ALL
    });
  }

  const updatedHtml = updateImageInHTML(resolved.section.html, edit, assetUri, '', {
    assetId,
    mimeType: file.type || '',
    pictureReplaceScope: edit.pictureReplaceScope || PICTURE_SCOPE_ALL,
    attribution: buildImageAttribution(meta)
  });
  if (updatedHtml === null) return false;

  store.update(resolved.path, updatedHtml, 'Replace image');
  if (!previewUrl) {
    eventBus.emit('preview:render-request', { preserveScroll: true });
  }
  eventBus.emit('editing:image-replaced', {
    instanceId: edit.instanceId,
    assetId,
    filename: file.name,
    kind: edit.kind || 'photo'
  });
  return true;
}

/**
 * Apply a direct image URL (no local asset record).
 * Used when remote hosts block browser file download by CORS.
 * @param {string} url
 * @param {{ source?: string, author?: string, authorUrl?: string }} [meta]
 */
async function applyImageUrl(url, _meta, target = null) {
  const edit = target || activeImageEdit;
  if (!edit || !url) return;

  const validity = validateReplacementTarget(edit);
  if (!validity.ok) {
    eventBus.emit('editing:error', { message: `Replacement skipped: ${validity.reason}` });
    return false;
  }
  const resolved = resolveSectionByInstance(edit.instanceId);
  if (!resolved) return false;

  const altText = '';
  const attribution = buildImageAttribution(_meta);

  if (edit.editableType === 'css-background') {
    const updatedCss = replaceSectionCssBackgroundImage(
      resolved.section.css,
      resolved.section.sectionId,
      edit.targetRef || edit.cssBgIndex,
      url
    );
    if (updatedCss === null) return false;

    store.update(`${resolved.sectionPath}.css`, updatedCss, 'Replace background image');
    eventBus.emit('preview:render-request', { preserveScroll: true });
    eventBus.emit('editing:image-replaced', {
      instanceId: edit.instanceId,
      externalUrl: url,
      kind: edit.kind || 'background'
    });
    return true;
  }

  bridge.send('update-image', {
    instanceId: edit.instanceId,
    editableIndex: edit.editableIndex,
    eid: edit.eid || edit.targetRef?.eid || '',
    src: url,
    alt: altText,
    mimeType: '',
    pictureReplaceScope: edit.pictureReplaceScope || PICTURE_SCOPE_ALL
  });

  const updatedHtml = updateImageInHTML(resolved.section.html, edit, url, altText, {
    assetId: '',
    mimeType: '',
    pictureReplaceScope: edit.pictureReplaceScope || PICTURE_SCOPE_ALL,
    attribution
  });
  if (updatedHtml === null) return false;

  store.update(resolved.path, updatedHtml, 'Replace image');
  eventBus.emit('editing:image-replaced', {
    instanceId: edit.instanceId,
    externalUrl: url,
    kind: edit.kind || 'photo'
  });
  return true;
}

/**
 * Store file as asset, swap active SVG element to <img>, trigger render.
 * Shared helper — consolidates logic from swapSvgToImage and swapSvgToStockPhoto.
 * @param {File} file
 * @param {{ source?: string, author?: string, authorUrl?: string }} [meta]
 */
async function applySvgToImageFile(file, meta) {
  const owner = activeSvgEdit;
  const captured = snapshotReplacementTarget(owner);
  if (!captured.ok) return false;
  const edit = captured.target;

  const assetId = await store.assets.store(file, meta);
  const assetUri = `pb-asset://${assetId}`;
  if (activeSvgEdit !== owner) return false;
  const validity = validateReplacementTarget(edit, { mimeType: file.type || '' });
  if (!validity.ok) {
    eventBus.emit('editing:error', { message: `Replacement skipped: ${validity.reason}` });
    return false;
  }

  const resolved = validity.resolved;
  if (!resolved) return false;

  const imgTag = `<img src="${assetUri}" alt="" data-pb-asset="${assetId}" />`;
  const updatedHtml = swapEditableInHTML(resolved.section.html, edit.editableIndex, imgTag, 'SVG');
  if (updatedHtml === null) return false;

  const oldEid =
    edit.eid ||
    findEditableByIndex(resolved.section.html, edit.editableIndex)?.getAttribute('data-pb-eid') ||
    '';

  store.update(resolved.path, updatedHtml, 'Swap SVG to image');
  showUndoToast('SVG swapped to image');
  if (
    !replaceEditableLiveFromHtml(edit.instanceId, updatedHtml, oldEid, {
      animate: 'image-replace'
    })
  ) {
    eventBus.emit('preview:render-request', { preserveScroll: true });
  }

  const imgData = { ...owner, editableType: 'image' };
  activeSvgEdit = null;
  activeImageEdit = imgData;
  eventBus.emit('editing:image-replaced', {
    instanceId: imgData.instanceId,
    assetId,
    filename: file.name,
    kind: imgData.kind || 'icon',
    undoToastHandled: true
  });
  eventBus.emit('editing:image-selected', imgData);
  return true;
}

/**
 * Swap active SVG element to an <img> that points at a direct external URL.
 * @param {string} url
 */
async function applySvgToImageUrl(url) {
  if (!activeSvgEdit || !url) return;

  const validity = validateReplacementTarget(activeSvgEdit);
  if (!validity.ok) {
    eventBus.emit('editing:error', { message: `Replacement skipped: ${validity.reason}` });
    return false;
  }
  const resolved = resolveSectionByInstance(activeSvgEdit.instanceId);
  if (!resolved) return false;

  const altText = inferNameFromUrl(url, 'Image');
  const imgTag = `<img src="${escapeHTML(url)}" alt="" />`;
  const updatedHtml = swapEditableInHTML(
    resolved.section.html,
    activeSvgEdit.editableIndex,
    imgTag,
    'SVG'
  );
  if (updatedHtml === null) return false;

  const oldEid =
    activeSvgEdit.eid ||
    findEditableByIndex(resolved.section.html, activeSvgEdit.editableIndex)?.getAttribute(
      'data-pb-eid'
    ) ||
    '';

  store.update(resolved.path, updatedHtml, 'Swap SVG to image');
  showUndoToast('SVG swapped to image');
  if (
    !replaceEditableLiveFromHtml(activeSvgEdit.instanceId, updatedHtml, oldEid, {
      animate: 'image-replace'
    })
  ) {
    eventBus.emit('preview:render-request', { preserveScroll: true });
  }

  const imgData = { ...activeSvgEdit, editableType: 'image' };
  activeSvgEdit = null;
  activeImageEdit = imgData;
  eventBus.emit('editing:image-replaced', {
    instanceId: imgData.instanceId,
    filename: altText,
    externalUrl: url,
    kind: imgData.kind || 'icon',
    undoToastHandled: true
  });
  eventBus.emit('editing:image-selected', imgData);
  return true;
}

/**
 * Open the unified image picker to choose a replacement image.
 * Returns a Promise that resolves on pick or cancel (for busy-state UX in section-list.js).
 */
export async function replaceImage(target = null) {
  const captured = snapshotReplacementTarget(target || activeImageEdit);
  if (!captured.ok) return Promise.resolve();
  const edit = captured.target;

  const pictureReplaceScope = await choosePictureReplaceScope(edit);
  if (!pictureReplaceScope || !validateReplacementTarget(edit).ok) return Promise.resolve();
  const scopedEdit = { ...edit, pictureReplaceScope };
  if (!target) activeImageEdit = { ...activeImageEdit, pictureReplaceScope };

  // The QUEUE: every image in this section, the clicked one first in line.
  // One pick fills one slot and the picker advances to the next EMPTY one, so
  // dressing a hero band is one dialog rather than four round trips through
  // click → Replace → search → pick.
  const slots = collectSectionImageSlots(scopedEdit).map((slot) => {
    const guarded = snapshotReplacementTarget(slot.data.edit);
    if (!guarded.ok) {
      throw new Error(`Replacement queue target is unavailable: ${guarded.reason}`);
    }
    return { ...slot, data: { ...slot.data, edit: guarded.target } };
  });

  return showImagePicker(
    async ({ file, url, source, author, authorUrl, slot }) => {
      // `slot` is the picker's statement about WHICH target this pick is for,
      // and it is the only thing that may decide: reading `scopedEdit` here
      // instead would send every pick in a multi-slot run to the image the
      // user happened to click first. It is absent for a single-slot open, so
      // the fallback is the original edit and that path is unchanged.
      const dest = slot?.data?.edit || scopedEdit;
      const validity = validateReplacementTarget(dest, { mimeType: file?.type || '' });
      if (!validity.ok) {
        throw Object.assign(new Error(`Replacement skipped: ${validity.reason}`), {
          userMessage: 'That element changed while the picker was open. Nothing was replaced.'
        });
      }
      if (file) {
        await applyImageFile(file, { source, author, authorUrl }, dest);
        return;
      }
      if (url) {
        await applyImageUrl(url, { source, author, authorUrl }, dest);
        return;
      }
      throw new Error('No image payload provided');
    },
    { ...getImagePickerContextForEdit(scopedEdit), ...(slots.length > 1 ? { slots } : {}) }
  );
}

/**
 * Every editable `<img>` in the edit's section, as picker slots — the clicked
 * one first so the queue starts where the user pointed.
 *
 * The rules live in `image-slot-queue.js`, which is pure and therefore
 * testable; this resolves the section and supplies the one predicate that is
 * this file's business. Decorative SVGs are `<img data-pb-editable="image">`
 * but a click on one routes to the decorative panel rather than to this picker
 * (`shouldRouteImageClickToSvgPanel`), so queueing one would offer a photo for
 * a slot that cannot take a photo.
 */
function collectSectionImageSlots(edit) {
  if (!edit?.instanceId) return [];
  const resolved = resolveSectionByInstance(edit.instanceId);
  return buildImageSlots(resolved?.section?.html || '', edit, {
    isDecorative: isDecorativeSvgImage
  });
}

function getImagePickerContextForEdit(edit) {
  if (!edit) return {};
  // MK-4a: this function already resolved the section to find the element, so
  // the sentence the header needs was one line away and was being thrown out.
  // A picker states its target (PP-0); a dialog that cannot fill that in is a
  // sign the caller discarded the information.
  const out = {};
  out.noun = kindLabel(
    edit.kind || (edit.editableType === 'css-background' ? 'background' : 'photo')
  );
  const context = describeImageEditTarget(edit);
  if (context) out.context = context;
  const target = getEditableElementForEdit(edit);
  if (edit.initialTab) out.initialTab = edit.initialTab;
  if (edit.initialCategory) out.initialCategory = edit.initialCategory;
  if (!target || String(target.tagName || '').toUpperCase() !== 'IMG') {
    if (edit.currentUrl) out.currentImageUrl = edit.currentUrl;
    return out;
  }
  const src =
    target.getAttribute('src') || target.getAttribute('data-src') || edit.currentUrl || '';
  if (src) out.currentImageUrl = src;
  return out;
}

function getImagePickerContextForSvgEdit(edit) {
  if (!edit) return {};
  const context = describeMediaEditTarget(edit);
  return context ? { context } : {};
}

/** "Replacing an image in Hero", from whatever the edit actually knows. */
function describeImageEditTarget(edit) {
  return describeMediaEditTarget(edit);
}

function describeMediaEditTarget(edit) {
  if (!edit?.instanceId) return '';
  const resolved = resolveSectionByInstance(edit.instanceId);
  const name = formatSectionName(resolved?.section);
  if (!name || name === 'Section') return '';
  const targetLabel = kindLabel(
    edit.kind ||
      (edit.editableType === 'css-background'
        ? 'background'
        : edit.editableType === 'svg'
          ? 'icon'
          : 'photo')
  ).toLowerCase();
  const article = /^[aeiou]/i.test(targetLabel) ? 'an' : 'a';
  return `Replacing ${article} ${targetLabel} in ${name}`;
}

/**
 * Open the SVG library modal for the user to pick a replacement SVG.
 */
export async function openSvgLibrary({ triggerEl = null } = {}) {
  const owner = activeSvgEdit;
  const captured = snapshotReplacementTarget(owner);
  if (!captured.ok) return;
  const focusEl =
    triggerEl || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
  const requestToken = ++svgModalRequestToken;
  showSvgModal([], false, { loading: true, triggerEl: focusEl });

  try {
    const svgs = await loadSvgIndex();
    if (requestToken !== svgModalRequestToken || !svgModal) return;
    if (activeSvgEdit !== owner || !validateReplacementTarget(captured.target).ok) {
      closeSvgModal();
      return;
    }
    showSvgModal(svgs, false, { triggerEl: focusEl });
    beginSvgModalOutcome();
  } catch (err) {
    if (requestToken === svgModalRequestToken) {
      closeSvgModal({ restoreFocus: false, invalidatePending: false });
    }
    console.error('[ImageEditor] Failed to load SVG library:', err);
    eventBus.emit('editing:error', { message: 'Failed to load SVG library' });
  }
}

/**
 * Open the decorative SVG library for the currently selected SVG.
 * Used for waves, dividers, masks, and other section decoratives where the
 * target's layout class must be preserved.
 */
export async function openDecorativeSvgLibraryForActiveSvg({
  slots = null,
  context = '',
  triggerEl = null
} = {}) {
  const owner = activeSvgEdit;
  const captured = snapshotReplacementTarget(owner);
  if (!captured.ok) return;
  const focusEl =
    triggerEl || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
  const requestToken = ++svgModalRequestToken;
  showSvgModal([], false, {
    loading: true,
    triggerEl: focusEl,
    library: 'decorative',
    slots,
    context
  });

  try {
    const svgs = await loadSvgIndex('decorative');
    if (requestToken !== svgModalRequestToken || !svgModal) return;
    if (activeSvgEdit !== owner || !validateReplacementTarget(captured.target).ok) {
      closeSvgModal();
      return;
    }
    showSvgModal(svgs, false, {
      triggerEl: focusEl,
      library: 'decorative',
      slots,
      context,
      onPick: (markup, entry, slot) =>
        replaceSvgForTarget(markup, slot?.data?.target || captured.target, {
          preserveTargetClass: true,
          decorativeName: entry?.name || '',
          decorativeFilename: entry?.filename || '',
          closeModal: false
        })
    });
    beginSvgModalOutcome();
  } catch (err) {
    if (requestToken === svgModalRequestToken) {
      closeSvgModal({ restoreFocus: false, invalidatePending: false });
    }
    console.error('[ImageEditor] Failed to load decorative SVG library:', err);
    eventBus.emit('editing:error', { message: 'Failed to load decorative SVG library' });
  }
}

/**
 * Directly replace SVG with provided markup.
 */
export function replaceSvg(svgMarkup, options = {}) {
  if (!activeSvgEdit) return;
  const validity = validateReplacementTarget(activeSvgEdit, { mimeType: 'image/svg+xml' });
  if (!validity.ok) {
    eventBus.emit('editing:error', { message: `Replacement skipped: ${validity.reason}` });
    return;
  }

  const resolved = resolveSectionByInstance(activeSvgEdit.instanceId);
  if (!resolved) return;

  const updatedHtml = updateSvgInHTML(
    resolved.section.html,
    activeSvgEdit.editableIndex,
    svgMarkup,
    options
  );
  if (updatedHtml === null) return;

  const oldEid =
    activeSvgEdit.eid ||
    findEditableByIndex(resolved.section.html, activeSvgEdit.editableIndex)?.getAttribute(
      'data-pb-eid'
    ) ||
    '';

  store.update(resolved.path, updatedHtml, `Replace ${kindLabel(activeSvgEdit.kind || 'icon')}`);
  if (!replaceEditableLiveFromHtml(activeSvgEdit.instanceId, updatedHtml, oldEid)) {
    eventBus.emit('preview:render-request', { preserveScroll: true });
  }

  markSvgModalPicked();
  closeSvgModal();
  eventBus.emit('editing:svg-replaced', { kind: activeSvgEdit.kind || 'icon' });
}

/**
 * Open SVG library in swap mode — clicking an SVG calls swapImageToSvg instead of replaceSvg.
 */
export async function openSvgLibraryForSwap() {
  const owner = activeImageEdit;
  const captured = snapshotReplacementTarget(owner);
  if (!captured.ok) return;
  const triggerEl = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const requestToken = ++svgModalRequestToken;
  showSvgModal([], true, { loading: true, triggerEl });

  try {
    const svgs = await loadSvgIndex();
    if (requestToken !== svgModalRequestToken || !svgModal) return;
    if (activeImageEdit !== owner || !validateReplacementTarget(captured.target).ok) {
      closeSvgModal();
      return;
    }
    showSvgModal(svgs, true, { triggerEl });
    beginSvgModalOutcome();
  } catch (err) {
    if (requestToken === svgModalRequestToken) {
      closeSvgModal({ restoreFocus: false, invalidatePending: false });
    }
    console.error('[ImageEditor] Failed to load SVG library:', err);
    eventBus.emit('editing:error', { message: 'Failed to load SVG library' });
  }
}

/**
 * Open the standard SVG library in callback mode.
 */
export async function openSvgLibraryForPick({ onPick, triggerEl = null } = {}) {
  if (typeof onPick !== 'function') return;
  const focusEl =
    triggerEl || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
  const requestToken = ++svgModalRequestToken;
  showSvgModal([], false, { loading: true, triggerEl: focusEl });

  try {
    const svgs = await loadSvgIndex();
    if (requestToken !== svgModalRequestToken || !svgModal) return;
    showSvgModal(svgs, false, { triggerEl: focusEl, onPick });
    beginSvgModalOutcome();
  } catch (err) {
    if (requestToken === svgModalRequestToken) {
      closeSvgModal({ restoreFocus: false, invalidatePending: false });
    }
    console.error('[ImageEditor] Failed to load SVG library:', err);
    eventBus.emit('editing:error', { message: 'Failed to load SVG library' });
  }
}

/**
 * Open the decorative SVG library in insert/replace callback mode.
 */
/**
 * @param {{onPick: Function, triggerEl?: HTMLElement|null, context?: string}} opts
 *   `context` (DS-5) is PP-0's rule applied here: a picker states its target.
 *   Both callers already resolve a section before opening — the panel's Replace
 *   button and the + gap — so a dialog that could not fill it in was a sign the
 *   caller had thrown the information away.
 */
export async function openSvgLibraryForInsert({
  onPick,
  triggerEl = null,
  context = '',
  slots = null
} = {}) {
  if (typeof onPick !== 'function') return;
  const focusEl =
    triggerEl || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
  const requestToken = ++svgModalRequestToken;
  showSvgModal([], false, {
    loading: true,
    triggerEl: focusEl,
    library: 'decorative',
    context,
    slots
  });

  try {
    const svgs = await loadSvgIndex('decorative');
    if (requestToken !== svgModalRequestToken || !svgModal) return;
    showSvgModal(svgs, false, {
      triggerEl: focusEl,
      library: 'decorative',
      onPick,
      context,
      slots
    });
    beginSvgModalOutcome();
  } catch (err) {
    if (requestToken === svgModalRequestToken) {
      closeSvgModal({ restoreFocus: false, invalidatePending: false });
    }
    console.error('[ImageEditor] Failed to load decorative SVG library:', err);
    eventBus.emit('editing:error', { message: 'Failed to load decorative SVG library' });
  }
}

/**
 * Swap the active IMG editable for an inline SVG.
 * If IMG is inside a <picture>, replaces the entire <picture> wrapper.
 */
export function swapImageToSvg(svgMarkup) {
  if (!activeImageEdit) return;
  const validity = validateReplacementTarget(activeImageEdit, { mimeType: 'image/svg+xml' });
  if (!validity.ok) {
    eventBus.emit('editing:error', { message: `Replacement skipped: ${validity.reason}` });
    return false;
  }

  const resolved = resolveSectionByInstance(activeImageEdit.instanceId);
  if (!resolved) return;

  // Sanitize once up front so BOTH the persisted swap AND the live <picture>
  // bridge send below receive clean markup (library/Pixabay SVG is
  // attacker-controllable; the live send bypasses swapEditableInHTML).
  const cleanMarkup = sanitizeSvgMarkupForInline(svgMarkup);
  if (!cleanMarkup) return;

  const updatedHtml = swapEditableInHTML(
    resolved.section.html,
    activeImageEdit.editableIndex,
    cleanMarkup,
    'IMG'
  );
  if (updatedHtml === null) return;

  const currentEditable = findEditableByIndex(resolved.section.html, activeImageEdit.editableIndex);
  const oldEid = activeImageEdit.eid || currentEditable?.getAttribute('data-pb-eid') || '';
  const wasInsidePicture = !!currentEditable?.closest('picture');

  store.update(resolved.path, updatedHtml, 'Swap image to SVG');
  showUndoToast('Image swapped to SVG');
  let liveApplied = false;
  if (wasInsidePicture) {
    bridge.send('swap-image-to-svg', {
      instanceId: activeImageEdit.instanceId,
      editableIndex: activeImageEdit.editableIndex,
      svgMarkup: cleanMarkup
    });
    liveApplied = true;
  } else if (oldEid) {
    liveApplied = replaceEditableLiveFromHtml(activeImageEdit.instanceId, updatedHtml, oldEid);
  }
  if (!liveApplied) {
    eventBus.emit('preview:render-request', { preserveScroll: true });
  }
  markSvgModalPicked();
  closeSvgModal();

  const svgData = { ...activeImageEdit, editableType: 'svg' };
  activeImageEdit = null;
  activeSvgEdit = svgData;
  eventBus.emit('editing:svg-replaced', { kind: svgData.kind || 'icon' });
  eventBus.emit('editing:svg-selected', svgData);
  return true;
}

/**
 * Open the unified image picker to swap the active SVG for an image.
 * Returns a Promise that resolves on pick or cancel (for busy-state UX in section-list.js).
 */
export function swapSvgToImage() {
  const owner = activeSvgEdit;
  const captured = snapshotReplacementTarget(owner);
  if (!captured.ok) return Promise.resolve();

  return showImagePicker(async ({ file, url, source, author, authorUrl }) => {
    if (activeSvgEdit !== owner || !validateReplacementTarget(captured.target).ok) return;
    if (file) {
      await applySvgToImageFile(file, { source, author, authorUrl });
      return;
    }
    if (url) {
      await applySvgToImageUrl(url);
      return;
    }
    throw new Error('No image payload provided');
  }, getImagePickerContextForSvgEdit(activeSvgEdit));
}

// --- Helpers ---

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

function inferNameFromUrl(url, fallback = 'Image') {
  try {
    const parsed = new URL(url);
    const rawName = parsed.pathname.split('/').pop() || '';
    const decoded = decodeURIComponent(rawName).trim();
    return decoded || fallback;
  } catch {
    return fallback;
  }
}

function replaceEditableLiveFromHtml(instanceId, updatedHtml, eid, options = {}) {
  if (!instanceId || !updatedHtml || !eid) return false;
  const previewEl = findEditableByEid(updatedHtml, eid);
  if (!previewEl) return false;
  bridge.send('replace-editable-html', {
    instanceId,
    eid,
    html: previewEl.outerHTML,
    ...(options.animate ? { animate: options.animate } : {})
  });
  return true;
}

// --- SVG Index ---

async function loadSvgIndex(library = 'icons') {
  if (library === 'decorative') {
    if (decorativeSvgIndex) return decorativeSvgIndex;
    try {
      const json = await loadJSON('data/decorative-svg-index.json');
      decorativeSvgIndex = Array.isArray(json) ? json : json.entries || [];
      return decorativeSvgIndex;
    } catch {
      // Fall through
    }
    console.warn(
      '[ImageEditor] decorative-svg-index.json not found. Decorative SVG library unavailable.'
    );
    decorativeSvgIndex = [];
    return decorativeSvgIndex;
  }

  if (svgIndex) return svgIndex;

  try {
    svgIndex = await loadJSON('data/svg-index.json');
    return svgIndex;
  } catch {
    // Fall through
  }

  console.warn('[ImageEditor] svg-index.json not found. SVG library unavailable.');
  svgIndex = [];
  return svgIndex;
}

/**
 * The chrome the collection browser does not own: the source control, the
 * API-key prompt, the status line, the footer band and the Load More control.
 * Structure only — `showSvgModal` wires every listener, the same split the
 * shared module's own chrome file uses.
 *
 * PARITY WITH THE IMAGE PICKER IS THE POINT (2026-09-02, user-directed, third
 * request). These two dialogs do the same job — pick an asset for a slot — and
 * shipped as two different products: the picker put its search in the header
 * bar beside the title, its sources in a segmented pill at the head of the chip
 * row with a divider after it, and an upload tile in the grid's first cell,
 * while this surface stacked a `.pb-code-tabs` strip over a full-width toolbar
 * search and had no upload at all. Everything here now mirrors
 * `image-picker/session.js buildChrome` node for node, and the CSS families are
 * SHARED rather than copied (`.pb-picker-surface` in media-kit.css) — a second
 * opinion about the look is what let them drift the first time.
 */
function buildSvgModalChrome({ isDecorative, onSource }) {
  // DECORATIVE MODE HAS ONE SOURCE, AND `hidden` DID NOT DELIVER THAT.
  //
  // `tabs.hidden = true` was here from the beginning and did nothing:
  // `.pb-code-tabs { display: flex }` (builder.css:2567) is an AUTHOR rule, and
  // the UA's `[hidden] { display: none }` loses to it. So the decorative dialog
  // shipped a visible 666x31 "Library | Pixabay Vectors" strip with 74.5x30
  // buttons — and clicking the second put the user in a source this mode does
  // not have.
  //
  // It was invisible for the life of the surface because no harness surface had
  // ever opened decorative mode; the existing `svg-library` entry opens the ICON
  // library, where the row is supposed to be there. DS-0's fixture measured it
  // on its first run.
  //
  // NOT BUILT AT ALL, rather than hidden harder. A control that is not
  // constructed cannot be revealed by a future stylesheet, cannot be reached by
  // Tab, and cannot be found by a screen reader — none of which `display: none`
  // alone guarantees against the next author rule. `sources` stays null so the
  // kit's `chips.leading` hook returns nothing and the row has no head.
  let sources = null;
  let segmented = null;
  if (!isDecorative) {
    // MK-1a's primitive, exactly as the picker's scope control — role=radiogroup,
    // aria-checked, a roving tabindex and arrows that SELECT. The hand-rolled
    // `.pb-code-tab` pair it replaces had none of those.
    segmented = createSegmentedControl({
      options: [
        { value: 'library', label: 'Library' },
        { value: 'pixabay', label: 'Pixabay Vectors' }
      ],
      ariaLabel: 'SVG source',
      value: 'library',
      className: 'pb-svg-scopes',
      onChange: (tab) => onSource?.(tab)
    });
    sources = document.createElement('div');
    sources.className = 'pb-picker-sources pb-svg-sources';
    sources.appendChild(segmented.el);
  }

  return {
    sources,
    segmented,
    ...buildSvgKeyPrompt(),
    ...buildSvgFoot(),
    statusEl: buildSvgStatusLine()
  };
}

/**
 * The footer band, the picker's shape and the picker's classes: what the grid
 * holds on the left, who it came from on the right. It exists here for the same
 * reason it exists there — the count was announced to the live region and
 * painted nowhere, so a sighted user could not see what a screen reader was
 * being told.
 */
function buildSvgFoot() {
  const foot = document.createElement('div');
  foot.className = 'pb-image-picker-foot';
  const count = document.createElement('span');
  count.className = 'pb-image-picker-count';
  const attribution = document.createElement('small');
  attribution.className = 'pb-image-picker-attribution';
  foot.append(count, attribution);
  return { foot, count, attribution };
}

/**
 * The Pixabay key gate. Built for BOTH modes even though decorative mode has no
 * Pixabay tab to reach it — `showSvgKeyPrompt` / `hideSvgKeyPrompt` are called
 * unconditionally, and a null here would be a branch at every call site for a
 * node that costs nothing.
 */
function buildSvgKeyPrompt() {
  const keyPrompt = document.createElement('div');
  // BOTH families: the picker's, which owns the band's inset and its air, and
  // this surface's, which owns the layout and is what the geometry harness
  // captures. The message/title children were already the picker's — "the gate
  // prompt is one look for two dialogs" (media-kit.css header) — and only the
  // container had been left behind.
  keyPrompt.className = 'pb-image-picker-key-prompt pb-svg-key-prompt';
  keyPrompt.style.display = 'none';
  const keyMessage = document.createElement('div');
  keyMessage.className = 'pb-image-picker-key-message';
  const keyTitle = document.createElement('strong');
  keyTitle.className = 'pb-image-picker-key-title';
  keyTitle.textContent = 'Pixabay API key needed';
  const keyHint = document.createElement('small');
  keyHint.textContent = 'Get a free key at pixabay.com/api/docs/';
  keyMessage.append(keyTitle, keyHint);
  const keySettings = createButton({
    variant: 'bare',
    className: 'pb-svg-key-settings',
    label: 'Open Settings'
  });
  keyPrompt.append(keyMessage, keySettings);
  return { keyPrompt, keySettings };
}

function buildSvgStatusLine() {
  const status = document.createElement('div');
  // Same two-family rule as the key prompt above: the picker's class carries the
  // centring, the gutter and the `:empty { display: none }` that keeps a silent
  // dialog from paying for a line it is not using.
  status.className = 'pb-image-picker-status pb-svg-status';
  return status;
}

// --- SVG Modal ---

/**
 * The SVG library, on ui/collection-browser.js (CB-11).
 *
 * BOTH TABS migrate or neither does: a Library tab on the shared module with a
 * hand-rolled Pixabay grid beside it would be worse than the duplication it
 * was meant to remove. So the browser holds ONE record set and the tab decides
 * where those records come from — the local index, or a Pixabay search.
 *
 * THE ONE PLACE THE MODULE'S IN-PLACE FILTERING DOES NOT APPLY, and why:
 * `svg-index.json` is **3,576** entries and the decorative index is **1,150**.
 * The donor never rendered more than 200 cards for a query, and that cap is
 * load-bearing — 3,576 cards is 3,576 DOM nodes and as many lazy <img>s at
 * open. So this surface computes its own record set (filter + category + the
 * 200 cap) and re-feeds it through `setRecords` when the query or category
 * changes, and `filter: () => true` tells the module that everything it was
 * handed is a match. It is the donor's own cadence, kept deliberately.
 *
 * What must not be lost, and is tested in svg-library-modal-tests.js:
 *  · `role="listbox"` on the grid with `role="option"` on every card — a
 *    listbox whose children are not options is a broken listbox, and
 *    dialog-a11y-tests does not check the pairing;
 *  · the custom focus restore (`restoreFocus: false` + `svgModalTrigger`)
 *    across ALL SIX public entry points, not the one modal-convergence covers;
 *  · the per-card in-flight guard — a double click used to run two fetches and
 *    apply the SVG twice, i.e. two mutations and two undo entries for one
 *    intent (pinned by double-submit-tests.js);
 *  · Pixabay's abort-on-tab-switch, its generation guard, its four error codes
 *    and the key prompt's focus moves;
 *  · MIME-aware picking: an SVG goes inline, a raster routes to the image
 *    pipeline;
 *  · decorative mode's category pills, `preserveTargetClass` and
 *    `decorativeName`.
 */
function showSvgModal(
  svgs,
  isSwap = false,
  {
    loading = false,
    triggerEl = null,
    library = 'icons',
    onPick = null,
    context = '',
    slots = null
  } = {}
) {
  closeSvgModal({ restoreFocus: false, invalidatePending: false });
  if (triggerEl) svgModalTrigger = triggerEl;

  const isDecorative = library === 'decorative';
  const allEntries = Array.isArray(svgs) ? svgs : [];
  let activeTab = 'library';
  let activeCategory =
    isDecorative && allEntries.some((svg) => svg.category === 'divider') ? 'divider' : 'all';
  let pixabayPage = 0;
  let pixabayTotal = 0;
  let pixabayLoadMoreBtn = null;
  let pixabayResults = [];
  /** Matches beyond the rendered cap, so the disclosure row can name them. */
  let overflowCount = 0;
  /**
   * DS-5: how many caps past the first the user has asked for.
   *
   * RESET BY THE NEXT QUERY OR CHIP CHANGE, and that is the semantic that will
   * otherwise be found by a user rather than by a test: a raised cap surviving
   * into a different result set silently renders all 1,150 cards. The count the
   * row shows is always of the CURRENT result set, because `overflowCount` is
   * recomputed inside `libraryRecordsFor` on every query and every chip.
   */
  let revealedPages = 0;
  let chrome = null;
  let api = null;
  const owner = isSwap ? activeImageEdit : activeSvgEdit;
  const capturedOwner = onPick ? null : snapshotReplacementTarget(owner);
  const isCurrentPicker = () =>
    api?.overlay === svgModal &&
    (typeof onPick === 'function' ||
      (owner === (isSwap ? activeImageEdit : activeSvgEdit) &&
        capturedOwner?.ok &&
        validateReplacementTarget(capturedOwner.target).ok));
  /** The upload tile's file input, and the teardown for the dialog-wide drop. */
  let svgFileInput = null;
  let svgDropTeardown = null;

  svgModalRestoreFocusOnClose = true;

  const dialogTitle = isDecorative ? 'Decorative SVG Library' : 'SVG Library';
  const queueNoun = isDecorative ? 'Shape' : 'SVG';

  /** The donor's category list: fixed, not derived — a category with nothing
   *  in it today still has to be reachable. */
  const DECORATIVE_CATEGORIES = ['divider', 'blob', 'organic', 'abstract', 'geometric', 'nature'];
  const RENDER_CAP = 200;

  /**
   * The icon library's chips.
   *
   * Derived from the WHOLE index rather than from the rendered records, for the
   * same reason the picker's `extra` is a full option list: the render cap is
   * 200 of 3,576, so chips built from `records` would silently drop every
   * category whose members happen to fall past the cap. Ordered by size — the
   * chip row pins nothing (`pinnedCount: 0`) and puts the rest in the drawer, so
   * the order IS what the user sees first.
   */
  const iconCategories = (() => {
    if (isDecorative) return [];
    const counts = new Map();
    for (const entry of allEntries) {
      const key = String(entry?.category || '');
      if (key) counts.set(key, (counts.get(key) || 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([k]) => k);
  })();

  /**
   * @param {string} query
   * @param {{keepReveal?: boolean}} [opts] — `keepReveal` is the DISCLOSURE
   *   re-reading the same result set one cap wider. Every other caller is a
   *   NEW result set and resets the reveal.
   */
  function libraryRecordsFor(query, { keepReveal = false } = {}) {
    if (!keepReveal) revealedPages = 0;
    const matches = filterSvgLibraryEntries(allEntries, {
      library,
      category: activeCategory,
      query: String(query || '')
        .trim()
        .toLowerCase()
    });
    const shown = RENDER_CAP * (revealedPages + 1);
    overflowCount = Math.max(0, matches.length - shown);
    return matches.slice(0, shown);
  }

  /**
   * DS-5: the per-category counts the chips opted out of.
   *
   * `countOf: () => undefined` meant seven filters and no idea what was behind
   * any of them. Counted over the WHOLE library rather than the current result
   * set, because a chip's number answers "how much is there", not "how much
   * survives the search I have already typed" — the kit's own `buildChipModel`
   * counts are the latter, which is why this surface supplies its own.
   *
   * Computed once per open; `allEntries` does not change while the dialog lives.
   */
  const categoryCounts = (() => {
    const counts = new Map();
    // Seed every DECLARED category at zero first. The donor's list is fixed
    // rather than derived — "a category with nothing in it today still has to
    // be reachable" — so an empty one must show its 0 rather than showing
    // nothing beside six chips that do carry numbers. The icon library derives
    // its list from the corpus, so there is nothing to seed there.
    if (isDecorative) for (const key of DECORATIVE_CATEGORIES) counts.set(key, 0);
    for (const entry of allEntries) {
      const key = String(entry?.category || '');
      if (key) counts.set(key, (counts.get(key) || 0) + 1);
    }
    counts.set('all', allEntries.length);
    return counts;
  })();

  /**
   * `divider · 12:1 · cliffsidecuts.com` — the facts that separate two cards
   * sharing a display name.
   *
   * The ratio is rendered as `N:1` above 1 and `1:N` below it, never a raw
   * float: "0.42:1" is arithmetic, "1:2.4" is a shape. A ratio of exactly 1 is
   * omitted rather than printed as `1:1` on every square card, which would be a
   * column of identical text — the thing this line exists to avoid.
   */
  function decorativeMetaLine(entry, ratio) {
    const parts = [];
    if (entry?.category) parts.push(String(entry.category));
    if (ratio > 0 && Math.abs(ratio - 1) > 0.05) {
      parts.push(
        ratio >= 1
          ? `${ratio.toFixed(1).replace(/\.0$/, '')}:1`
          : `1:${(1 / ratio).toFixed(1).replace(/\.0$/, '')}`
      );
    }
    // 782 of 1,150 are scraped and carry one; the 368 curated do not.
    if (entry?.sourceTemplate) parts.push(String(entry.sourceTemplate));
    return parts.join(' · ');
  }

  /**
   * DS-8: the screen-reader channel, which this surface did not have.
   *
   * `.pb-svg-status` is a bare `<div>` with no `role` and no `aria-live` — so
   * every message this dialog has ever shown ("Searching…", "Rate limited",
   * "Invalid API key", and DS-7's per-pick "2 shapes left") was VISIBLE ONLY.
   * The kit builds an unconditional live region for exactly this (the MK-7
   * lesson: an affordance a shared component creates conditionally is created
   * for the first consumer's reason only), and `api.setStatus` is its writer.
   * The surface had simply never called it, because it had brought its own
   * status line along from the hand-rolled era.
   *
   * `announce` is the live region ALONE — used where a sentence belongs to a
   * screen reader and not to the visible chrome, such as a result count that
   * would otherwise appear under the grid on every keystroke.
   */
  function announce(text) {
    api?.setStatus(text);
  }

  /** The visible status line, and the announcement that goes with it. Anything
   *  the dialog SAYS out loud it also says to the live region. */
  function setStatus(text) {
    if (chrome?.statusEl) chrome.statusEl.textContent = text;
    announce(text);
  }

  /**
   * One announcement per state change (the DS-8 rule), for the state changes
   * that have no other voice: a query, a chip and a reveal all replace the
   * result set silently. `headerMeta: false` on this surface means there is no
   * `.pb-coll-meta` line — which is the kit's own `aria-live` count — so
   * without this a keyboard user typing into the search field is told nothing
   * at all, however many cards appear or disappear.
   */
  function announceLibraryCount(records) {
    const n = Array.isArray(records) ? records.length : 0;
    const noun = isDecorative ? 'shape' : 'icon';
    if (n === 0) {
      paintFoot('');
      announce(`No ${noun}s match your search.`);
      return;
    }
    const shown = `${n} ${n === 1 ? noun : `${noun}s`}`;
    // The cap is part of the count or the number is a lie: 200 of 1,150 read
    // as "200" is the same defect DS-5 fixed on the visible side.
    const line = overflowCount > 0 ? `${shown} shown, ${overflowCount} more` : shown;
    paintFoot(line);
    announce(line);
  }

  /**
   * The footer band — one derivation, two consumers.
   *
   * The picker's own rule, and the defect it was written for is present here
   * too: the count above was announced to the live region and painted NOWHERE,
   * so a sighted user could not see what a screen reader was being told.
   * Attribution is credited only where it is owed.
   */
  function paintFoot(countLine) {
    if (!chrome?.foot) return;
    chrome.count.textContent = String(countLine || '');
    chrome.attribution.textContent = activeTab === 'pixabay' ? 'Vectors from Pixabay' : '';
    chrome.foot.hidden = !chrome.count.textContent && !chrome.attribution.textContent;
  }

  /** Every library result-set change goes through here, so the announcement
   *  cannot be attached to two of the three callers and forgotten on the third. */
  function setLibraryRecords(query, opts) {
    const records = libraryRecordsFor(query, opts);
    api?.setRecords(records);
    announceLibraryCount(records);
  }

  // ── DS-4: the frame follows the art ────────────────────────────────────────
  //
  // MK's rule was that a thumbnail's FIT is a property of the asset. This
  // extends it to the CELL, because `contain` in a square cell cannot serve
  // both a 6:1 divider and a 1:1 blob — it renders the divider as a smear in a
  // mostly-empty box.
  //
  // Measured over `data/decorative-svg-index.json` (1,150 entries) on
  // 2026-08-29: aspect p50 1.21, p75 4.55, p95 10.73, max 51.9; **368 (32%) are
  // >= 2.5:1**; and among the 330 `divider` entries the MEDIAN is 6.08:1 with
  // 166 above 6:1. `activeCategory` defaults to `divider` whenever the corpus
  // has one, so the DEFAULT VIEW is the worst case.
  //
  // The ratio comes from `viewBox`, which is present on ALL 1,150 — so this
  // needs no fetch, no probe and no new data. Parsed once, in `adapt`.
  const WIDE_CELL_RATIO = 2.5;

  /** `viewBox` → width/height, or 0 when it is absent or degenerate. */
  function ratioFromViewBox(viewBox) {
    const parts = String(viewBox || '')
      .trim()
      .split(/[\s,]+/)
      .map(Number);
    if (parts.length !== 4) return 0;
    const [, , w, h] = parts;
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return 0;
    return w / h;
  }

  // ── DS-3: the preview tells the truth ──────────────────────────────────────
  //
  // The card used to be an `<img>` of the file, which is what the shape looks
  // like IN THE LIBRARY and not what it looks like after insert — the insert
  // flattened every fill and stroke. DS-2 made that a choice; this makes the
  // grid show the choice.
  //
  // TWO PATHS, and which one a card takes is a property of the ENTRY:
  //
  //   · Under `original`, the `<img>` is already correct for the 1,109 entries
  //     with `colorMode: 'baked'` — zero cost, no fetch, no DOM work. It is NOT
  //     correct for the 41 with `currentColor` or `mixed`: an <img> renders
  //     those in the UA's default black, while the inserted inline SVG inherits
  //     `color` from the section. Those 41 take the inline path in Original
  //     mode too. This is the whole reason `colorMode` is in the index, and it
  //     had never been read.
  //   · Under a TOKEN, every visible card goes inline, because the token is
  //     precisely what an <img> cannot show.
  //
  // Rejected and recorded: CSS `mask-image`. Cheaper, exact for the 780 entries
  // without `defs`, and a lie about the other 370 — `shouldRewriteSvgPaint`
  // deliberately skips `url(...)` paint, so a mask would report a gradient
  // shape as a solid silhouette.
  let activeColorToken = 'original';
  /**
   * Keyed by `filename`, holding the fetched SOURCE TEXT — never the
   * transformed output, which is a function of the token and would multiply by
   * the number of tokens the user tries.
   *
   * THAT CHOICE IS ALSO WHAT MAKES A STALE COLOUR STRUCTURALLY IMPOSSIBLE, and
   * it is worth stating because the plan called for a generation counter here
   * (on the `svgPixabayGeneration` precedent) to drop a fetch that resolves
   * after the user switched tokens. There is nothing to drop: the fetch is
   * token-INDEPENDENT, and the token is read at PAINT time, so a request issued
   * under `--primary` and landing under `--secondary` paints `--secondary` —
   * the right answer, not a stale one.
   *
   * The counter was written, and then deleted: with it removed no test could
   * tell, and a second mechanism no test can distinguish from the first is how
   * dead safety code accumulates.
   *
   * WHAT IS ACTUALLY LOAD-BEARING HERE, established by seeding each candidate:
   * the POOL's `task.img.isConnected` check. Removing it fails both this
   * surface's "a fetch still in flight when the dialog closes is dropped" and
   * the kit's own "a detached target is never written into". `previewPool.cancel()`
   * and the paint-time token read above are belt-and-braces — each can be
   * removed with no test noticing, because the others cover. They are kept
   * because they stop wasted work and keep the code simpler to read, NOT
   * because anything gates them; do not mistake either for a guarantee.
   */
  const previewSourceCache = new Map();
  let previewPool = null;

  /** Does this entry need the inline path at the current token? */
  function needsInlinePreview(entry) {
    if (!isDecorative || !entry || entry.__pixabay) return false;
    if (activeColorToken !== 'original') return true;
    return entry.colorMode === 'currentColor' || entry.colorMode === 'mixed';
  }

  async function fetchSvgSource(entry, { signal } = {}) {
    const key = String(entry?.filename || '');
    if (previewSourceCache.has(key)) return previewSourceCache.get(key);
    const resp = await fetch(getSvgAssetUrl(entry), signal ? { signal } : undefined);
    if (!resp.ok) throw new Error('Failed to fetch SVG');
    const text = await resp.text();
    previewSourceCache.set(key, text);
    return text;
  }

  /**
   * Repaint every visible card that needs the inline path.
   *
   * `buildPoster` is SYNCHRONOUS (`ui/collection-item-view.js:47-101`), so this
   * is a post-render update. It runs through the kit's pool — bounded at 4 and
   * wired to the browser's own abort signal — rather than a second unbounded
   * fetcher, and it writes through `api.updateItem`'s element, never into
   * `listEl` directly and never during a rebuild.
   */
  function paintInlinePreviews() {
    if (!isDecorative || !api) return;
    previewPool?.cancel();
    previewPool = createThumbnailPool({
      concurrency: 4,
      signal: api.openAbortSignal,
      resolve: (entry, ctx) => fetchSvgSource(entry, ctx),
      apply: (host, source, entry) => {
        // `activeColorToken` is read HERE, not captured when the task was
        // queued — see the cache's comment. Capturing it would reintroduce
        // exactly the stale-colour bug a counter would then be needed to
        // paper over.
        const markup = serializeDecorativePreview(source, { colorToken: activeColorToken });
        if (!markup) return;
        host.textContent = '';
        host.insertAdjacentHTML('beforeend', markup);
        host.dataset.pbPreviewToken = activeColorToken;
        host.dataset.pbPreviewFor = String(entry?.filename || '');
      }
    });

    for (const item of api.getItems()) {
      const entry = item.data;
      const el = api.itemEl(item.id);
      const poster = el?.querySelector('.pb-coll-poster');
      if (!poster) continue;
      if (!needsInlinePreview(entry)) {
        // Back to the <img>. Re-rendering the item is the kit's job, and doing
        // it here would fight `renderList`; the poster is repainted in place.
        if (poster.dataset.pbPreviewToken !== undefined) {
          poster.textContent = '';
          delete poster.dataset.pbPreviewToken;
          delete poster.dataset.pbPreviewFor;
          const img = document.createElement('img');
          img.alt = String(entry?.name ?? '');
          img.decoding = 'async';
          img.loading = 'lazy';
          img.src = getSvgAssetUrl(entry);
          poster.appendChild(img);
        }
        continue;
      }
      // Already showing this exact token for this exact file — nothing to do.
      if (
        poster.dataset.pbPreviewToken === activeColorToken &&
        poster.dataset.pbPreviewFor === String(entry?.filename || '')
      ) {
        continue;
      }
      previewPool.enqueue(entry, poster);
    }
  }

  function setColorToken(token) {
    if (token === activeColorToken) return;
    activeColorToken = token;
    paintInlinePreviews();
  }

  /**
   * The colour control (D2/D3).
   *
   * `createSegmentedControl`, never a hand-rolled `<select>` — MK-1a promoted
   * it and rule 16 of the sweep exists to stop the next one. "Original" is
   * first and selected, which is the whole decision G1 records.
   */
  const svgColorControl = isDecorative
    ? createSegmentedControl({
        ariaLabel: 'Preview colour',
        className: 'pb-svg-color',
        value: 'original',
        options: [
          { value: 'original', label: 'Original' },
          { value: '--primary', label: 'Primary' },
          { value: '--secondary', label: 'Secondary' },
          { value: '--headerColor', label: 'Heading' },
          { value: '--bodyTextColor', label: 'Body' }
        ],
        onChange: (value) => setColorToken(value)
      })
    : null;

  function showSvgKeyPrompt() {
    if (!chrome) return;
    chrome.keyPrompt.style.display = '';
    const searchInput = api?.searchInput;
    if (searchInput) searchInput.disabled = true;
  }

  function hideSvgKeyPrompt() {
    if (!chrome) return;
    chrome.keyPrompt.style.display = 'none';
    const searchInput = api?.searchInput;
    if (searchInput) searchInput.disabled = false;
  }

  api = openCollectionBrowser({
    // Kept for the dialog's ACCESSIBLE NAME (`aria-labelledby` points at the
    // <h2>); the kit's bar then hides it visually and the queue's title line
    // repeats it. Exactly the picker's arrangement.
    title: dialogTitle,
    // DS-7 + picker parity: the queue is passed ALWAYS now, `slots` and all.
    //
    // It used to be withheld below two slots so the header could not take over
    // — D12's "below 2 the dialog behaves byte-identically". That invariant is
    // what kept the search stacked under the title on every ordinary open,
    // which is the difference the user has now asked three times to remove: the
    // picker passes `queue` unconditionally and gets its one-row header for it.
    // The DEGRADATION that actually matters is untouched and is asserted
    // separately — `advanceQueueOrClose` still returns false below two slots,
    // so the dialog still closes on the first pick and no strip is painted.
    queue: {
      ...(Array.isArray(slots) && slots.length ? { slots } : {}),
      noun: queueNoun,
      ariaLabel: `${queueNoun} slots`,
      title: (slot) => slot?.label || dialogTitle,
      // Below two slots there is no position to report and nothing to count
      // down, so the sub falls back to the PP-0 context sentence the header
      // used to render on its own line ("Replacing a logo in navigation").
      sub: (slot, st) =>
        st.size >= 2
          ? [`${st.cursor + 1} of ${st.size}`, slot?.ratio, `${st.filledCount} filled`]
              .filter(Boolean)
              .join(' · ')
          : slot?.ratio || String(context || '')
    },
    testid: 'svg-library',
    // The donor's own box, not a size tier. Widened 700 → 1040 to match the
    // image picker: the two surfaces do the same job — pick an asset for a slot
    // — and at 700 with `dense` tracks a card was ~100px against the picker's
    // ~190px, so the same library read as a different, cheaper product.
    size: null,
    maxWidth: 1040,
    // TWO families. `pb-svg-modal` is this surface's own (the colour control's
    // wrap, the card, the wide cell) and is what the geometry harness captures;
    // `pb-picker-surface` is the SHARED band system the image picker also
    // carries — dialog height, zeroed body padding, the chip/scroll/gate
    // gutters, the tile dressing and the drop outline. Sharing the class rather
    // than copying its rules is the whole reason the two dialogs can no longer
    // drift apart.
    className: 'pb-svg-modal pb-picker-surface',
    // This surface restores focus itself, to whichever of SIX entry points
    // invoked it — `svgModalTrigger`, applied in finalizeSvgModalClose.
    restoreFocus: false,
    // DS-8: open-time focus goes through `initialFocus` only (the PP-0 rule),
    // and ONLY on the data open.
    //
    // `undefined` is the kit's default (its first focusable), not "no focus" —
    // `collection-browser.js` omits the key entirely when the value is
    // undefined, so `createModal` behaves exactly as it did before this line
    // existed. That distinction is the whole reason this is not a constant:
    // the loading open DISABLES the search input a few lines below, and
    // `focusInitialIn` would hand it a real node whose `.focus()` is a no-op —
    // so the fallback to the trap's first focusable never runs and the dialog
    // opens with focus stranded on the trigger behind it. `false` would be the
    // same regression stated explicitly.
    initialFocus: loading ? undefined : 'search',
    layout: 'grid',
    // `media`, the image picker's own track (178px), not `dense` (100px). The
    // original reasoning — "an icon grid, not a poster grid" — is sound for an
    // icon but wrong for the surface: half these entries are dividers, blobs
    // and masks whose whole distinguishing feature is shape, and at 100px a
    // wave and a divider were the same grey smear. Matching the picker's track
    // is also what lets the caption sit under the art instead of colliding
    // with its neighbour.
    density: 'media',
    listTag: 'div',
    itemTag: 'div',
    listRole: 'listbox',
    listAriaLabel: 'SVG library',
    itemRole: 'option',
    headerMeta: false,
    search: {
      placeholder: 'Search SVGs...',
      ariaLabel: 'Search SVGs',
      debounceMs: 200,
      onQuery: (query) => {
        if (activeTab === 'library') setLibraryRecords(query);
        else doPixabaySearch(query, 1);
      }
    },
    // ONE ROW, the picker's: source segments at the head, a divider, then the
    // category chips. The chip row is now UNCONDITIONAL — it used to be `false`
    // in icon mode, which is why that mode had no row at all and therefore
    // nowhere to park a source control. `filterSvgLibraryEntries` stays the one
    // place that knows what a category means in either mode.
    chips: {
      ariaLabel: isDecorative ? 'Decorative SVG categories' : 'Filter by category',
      // The source control, re-parked at the head of the row on every repaint
      // through the kit's `leading` hook — never prepended by hand, because
      // `renderChipRow` starts with `textContent = ''` and a hand-prepended
      // control survives exactly until the first chip click. Safe to read
      // `chrome` lazily: `onOpen` runs before the first load and therefore
      // before the first paint of this row.
      leading: () => chrome?.sources || null,
      keysOf: () => [],
      // Decorative's list is FIXED (a category with nothing in it today still
      // has to be reachable); the icon library's is derived once from the whole
      // index, not from the 200-card render cap, so a category is not hidden by
      // the cap that hides its members. Pixabay has no categories at all, which
      // is the picker's Stock scope exactly — the row is just `all` there.
      extra: () =>
        activeTab === 'pixabay' ? [] : isDecorative ? DECORATIVE_CATEGORIES : iconCategories,
      pinnedCount: 0,
      activeKey: activeCategory,
      labelOf: (key) => (key === 'all' ? 'All' : key[0].toUpperCase() + key.slice(1)),
      // DS-5: the real per-category count. It was `() => undefined`, so seven
      // filters shipped with no idea what was behind any of them.
      countOf: (key) => categoryCounts?.get(key),
      // DS-3: the colour control rides the kit's `trailing` hook, which is
      // an OWNED node the chip row re-parks on every repaint. Prepending it
      // by hand would work exactly until the first chip click, because
      // `renderChipRow` starts with `textContent = ''` — the trap the image
      // picker found the hard way with its scope control.
      trailing: () => svgColorControl?.el || null,
      onChange: (key) => {
        activeCategory = key;
        if (activeTab === 'library') setLibraryRecords(api.getQuery());
      }
    },
    // The upload TILE, in the grid's first cell — the picker's `Upload an
    // image` dropzone, for SVGs. The whole dialog is the drop target (wired in
    // `onOpen`); this is the click route to the same handler.
    tiles: {
      leading: [
        {
          id: 'upload',
          kind: 'create',
          label: 'Upload an SVG',
          description: 'SVG',
          onActivate: () => svgFileInput?.open()
        }
      ]
    },
    // DS-5: the cap is disclosed through the KIT's primitive, and it now has a
    // WAY PAST ITSELF — "refine your search" was the only escape from a 200-cap
    // over a 1,150-entry library. `onRender` is a seam, not a home (the PP-0
    // rule), and the hand-built `.pb-svg-more` row it used to build is gone.
    //
    // `hidden` is DERIVED on every paint from `overflowCount`, which
    // `libraryRecordsFor` recomputes for the current result set — so the number
    // is never stale, and the kit already refuses to show the row when nothing
    // is visible (at zero the empty state owns the single reveal control).
    ...(isDecorative
      ? {
          disclosure: {
            hidden: () => overflowCount,
            label: (n) => `Show ${n} more`,
            onReveal: () => {
              revealedPages += 1;
              setLibraryRecords(api.getQuery(), { keepReveal: true });
            }
          }
        }
      : {}),
    // The surface owns visibility: everything it feeds is already a match, and
    // the record set is capped (see the header).
    filter: () => true,
    skeleton: { variant: 'image', count: () => 12 },
    illustration: 'images',
    emptyCopy: { title: 'No SVGs in this library.' },
    filteredEmptyCopy: () => ({
      title: 'No SVGs match your search',
      body: isDecorative
        ? 'Try another category or search for "wave", "blob", or "leaf"'
        : 'Try a broader term like "arrow" or "icon"'
    }),

    load: async () => libraryRecordsFor(''),
    idOf: (entry, i) => String(entry?.filename || entry?.id || i),

    adapt: (entry) => {
      // Pixabay results and library entries are the same card; the difference
      // is where the thumbnail comes from and what a pick does.
      const isPixabay = !!entry.__pixabay;
      const name = isPixabay ? entry.author || entry.title : entry.name;
      // DS-4. A Pixabay result has no `viewBox` and is a raster preview anyway,
      // so it keeps the square cell.
      const ratio = isPixabay ? 0 : ratioFromViewBox(entry.viewBox);
      const isWide = ratio >= WIDE_CELL_RATIO;
      // MK-2c: the kit's poster, not a hand-built img + span. Every SVG here
      // is a vector on a transparent ground, so this surface is the second
      // consumer that earns `poster.fit` / `poster.background` — and it pays
      // down one of MK-1c's checkerboard copies at the same time.
      return {
        id: String(entry.filename || entry.id || name),
        // The VISIBLE caption is `name`, as it was when it was a hand-built
        // <span>; `title` is what the kit paints and what search highlights.
        // For a Pixabay result that is the author, which is what must be
        // shown — its `entry.name` is undefined.
        title: String(name ?? ''),
        titleAttr: String(entry.name || entry.title || ''),
        // DS-5: a facts line that TELLS CARDS APART. 196 collision groups cover
        // 589 of the 1,150 entries — `quote` collides 55 ways — and `adapt`
        // returned `title` only, so half the library was rows of identical
        // words. Category and aspect come free from the index; the source
        // template is what separates two scrapes of the same shape.
        //
        // The MK rule is that the facts line must DISCRIMINATE: `divider · 12:1`
        // does, a provenance string identical on four consecutive cards does not
        // — which is why the ratio leads and the template follows it.
        ...(isPixabay || !isDecorative
          ? {}
          : { meta: decorativeMetaLine(entry, ratio), metaAttr: entry.sourceTemplate || '' }),
        // DS-4: a wide shape gets a wide cell. The class spans two media grid
        // columns; the exact ratio rides `--pb-coll-art-ratio` below, so a 4:1
        // divider and a 12:1 one are not both letterboxed to the same box. A
        // shape at 51.9:1 IS still letterboxed — the cell approximates the art,
        // it does not chase it.
        //
        // DECORATIVE ONLY as of the picker-parity pass. In the icon library
        // every cell is now the picker's own 3:2 frame, because "same size
        // containers" is the whole point of that change and a ragged grid is
        // the most visible way to fail it. DS-4's actual complaint — a 6:1
        // divider as a 17px smear in a 105px square — is answered by the frame
        // itself: at the media track a 6:1 shape renders ~178x30 inside it.
        className: isWide && isDecorative ? 'pb-svg-card pb-svg-card-wide' : 'pb-svg-card',
        poster: {
          kind: 'image',
          src: isPixabay ? entry.thumb : getSvgAssetUrl(entry),
          alt: String((isPixabay ? entry.title : entry.name) ?? ''),
          fit: 'contain',
          background: 'checker',
          // The picker's frame, and the reason the two grids finally measure
          // the same: a record card there is `poster.aspect: 'wide'` filling the
          // track, where this surface pinned a 68px square inside it and read as
          // a different, cheaper product at the same track width.
          aspect: 'wide',
          // Written inline as a `--pb-coll-*` custom property, following the
          // `poster.hue → --pb-coll-hue` precedent exactly
          // (collection-item-view.js:70) and registered in RUNTIME_SET_TOKENS,
          // so COLLECTION-1 stays satisfied: a --pb-coll-* defined in a feature
          // sheet would be a private fork of the collection register.
          //
          // CLAMPED at 6, not passed through. Past that the cell would have to
          // be wider than the 700px dialog to gain anything, and an unclamped
          // 51.9 collapses the poster to a 2px-tall line.
          ...(isWide && isDecorative ? { artRatio: Math.min(ratio, 6) } : {})
        },
        data: entry
      };
    },

    // The card IS the control: a listbox option is picked by clicking it, and
    // by Enter through the module's grid key nav.
    activateOnItemClick: true,
    onActivate: (item) => pickEntry(item),

    onOpen(handle) {
      api = handle;
      handle.body.classList.add('pb-svg-modal-body');
      chrome = buildSvgModalChrome({
        isDecorative,
        onSource: (tab) => {
          if (tab !== activeTab) switchTab(tab);
        }
      });
      const shell = handle.listEl.closest('.pb-coll-shell');
      const scroller = handle.listEl.closest('.pb-coll-scroll');
      // The picker's band order, and nothing is inserted at the head of the
      // shell any more: the SOURCE control rides `chips.leading`, which is an
      // OWNED node the chip row re-parks on every repaint. A row inserted here
      // by hand is a row that survives until the first chip click.
      //
      // Load More is appended after the SCROLLER when a page arrives — NEVER
      // inside the list's flex row, which is where the donor put it:
      // `.pb-svg-library-wrap` is `display:flex` and `grid.after(button)` made
      // the button a flex sibling, so it took the row beside the results and
      // squeezed them into a 24px column.
      if (scroller) {
        shell?.insertBefore(chrome.keyPrompt, scroller);
        scroller.after(chrome.statusEl);
        chrome.statusEl.after(chrome.foot);
      }

      chrome.keySettings.addEventListener('click', () => {
        closeSvgModal({ restoreFocus: false });
        eventBus.emit('props:request-tab', { tab: 'client-seo' });
        eventBus.emit('client-seo:focus-image-source-settings', { provider: 'pixabay' });
      });

      // ── Upload (picker parity) ──────────────────────────────────────────
      // The tile is the click route; the WHOLE dialog is the drop target, for
      // the reason MK-4a gives — dropping onto the grid is the obvious gesture
      // once the grid is what fills the dialog, and a pane-only target does
      // nothing there. `accept` narrows the file dialog; `handleSvgUpload`
      // is still the gate, because a user can always choose "All Files".
      svgFileInput = createFileInput((file) => handleSvgUpload(file), {
        accept: 'image/svg+xml,.svg'
      });
      handle.body.appendChild(svgFileInput.el);
      svgDropTeardown = installDropTarget(handle.modal || handle.body, (file) =>
        handleSvgUpload(file)
      );
    },

    onRender() {
      // DS-5: the cap's row is the kit's `disclosure` now. Nothing is built
      // here — `onRender` is a seam, not a home.
      //
      // DS-3: `renderList` replaces the list's children, so every inline
      // preview it just painted is gone. Re-arming from the writer is the MK-7
      // `gridKeyNav` rule — a one-shot pass over the DOM must be re-run by
      // whatever rebuilds it.
      paintInlinePreviews();
    },

    onClose: () => {
      // The two nodes this surface owns outside the kit's tree. `installDropTarget`
      // hangs four listeners on the modal and `createFileInput` one on an input
      // parked in the body — both would otherwise outlive the dialog that made
      // them, which is exactly the leak `cleanup()` exists for elsewhere.
      svgDropTeardown?.();
      svgDropTeardown = null;
      svgFileInput?.destroy();
      svgFileInput = null;
      finalizeSvgModalClose(null, { fromModule: true });
    }
  });
  if (!api) return;
  svgModalKit = { close: () => api.close(), overlay: api.overlay };
  svgModal = api.overlay;

  // --- Loading state -------------------------------------------------------
  // The six entry points open the dialog empty and re-open it with data; the
  // first call paints the module's skeleton, which is what the old hand-rolled
  // "Loading SVG library…" line stood in for.
  if (loading) {
    const searchInput = api.searchInput;
    if (searchInput) {
      searchInput.disabled = true;
      searchInput.setAttribute('aria-disabled', 'true');
    }
    return;
  }

  // --- Picking -------------------------------------------------------------

  /**
   * One pick path for both tabs. The in-flight guard is per CARD and it is the
   * donor's: without it a double click ran two fetches and applied the SVG
   * twice — two real mutations and two undo entries for one user intent.
   */
  async function pickEntry(item) {
    if (!isCurrentPicker()) return;
    const el = api.itemEl(item.id);
    if (el?.classList.contains('pb-loading')) return;
    el?.classList.add('pb-loading');
    const entry = item.data;
    try {
      if (entry.__pixabay) {
        await handlePixabayPick(entry, isSwap);
        return;
      }
      const assetUrl = getSvgAssetUrl(entry);
      const resp = await fetch(assetUrl);
      if (!resp.ok) throw new Error('Failed to fetch SVG');
      await applySvgMarkup(await resp.text(), entry, el);
    } catch (err) {
      // Re-arm so the user can retry; the modal stays open on failure.
      el?.classList.remove('pb-loading');
      reportError('SVG library', err, {
        user: 'toast',
        message: err?.userMessage || 'Could not load that SVG. Try another, or pick again.'
      });
    }
  }

  /**
   * Apply one piece of SVG markup, whatever produced it.
   *
   * EXTRACTED FROM `pickEntry`, unchanged, so the upload tile lands through the
   * same four branches a grid pick does — callback mode, image→SVG swap,
   * decorative replace, plain replace — rather than growing a fifth path that
   * would be the only one nothing tests. `el` is the card to re-arm when the
   * queue keeps the dialog open; an upload has no card and passes null.
   */
  async function applySvgMarkup(markup, entry, el) {
    if (!isCurrentPicker()) return;
    if (typeof onPick === 'function') {
      // DS-7: THE QUEUE PATH IS ADDITIVE, and that is the whole safety
      // argument. `closeSvgModal()` has run unconditionally on the line after
      // `onPick` for the life of this dialog, and ten call sites rely on it.
      // With NO queue — `api.queue` null, or present and already drained on
      // the first fill — this is byte-identical to what it was, which is the
      // same degradation hinge `isDrained()` gives the image picker.
      const slot = api?.queue?.current() || null;
      await onPick(markup, entry, slot);
      if (api?.overlay !== svgModal) return;
      markSvgModalPicked();
      if (advanceQueueOrClose(slot)) {
        // RE-ARM THE CARD. The per-card in-flight guard (`pb-loading`) exists
        // to stop a double click running two fetches and two mutations for
        // one intent — `double-submit-tests.js` pins it — and it was written
        // for a dialog that CLOSES on the first pick, so it never needed
        // clearing. Under a queue the dialog stays open, and leaving the flag
        // on permanently disables every card the user has already used:
        // picking the same shape for two slots silently did nothing.
        //
        // Found by driving the queue to drained rather than by reading the
        // option back, which is the warning image-picker-tests.js records.
        el?.classList.remove('pb-loading');
        return;
      }
      closeSvgModal();
    } else if (isSwap) {
      swapImageToSvg(markup);
    } else if (isDecorative) {
      replaceSvg(markup, {
        preserveTargetClass: true,
        decorativeName: entry?.name || ''
      });
    } else {
      replaceSvg(markup);
    }
  }

  /**
   * The upload tile and the dialog-wide drop, one handler.
   *
   * SANITIZED BEFORE IT IS APPLIED, and that is not optional here the way it is
   * for a library entry: every other source in this dialog is markup this repo
   * produced or Pixabay served, and this one is a file off the user's disk
   * going straight into the page. It goes through the same
   * parse → sanitize → serialize the Pixabay SVG path already uses
   * (`svg-sanitizer.js`), so script, event handlers and foreign namespaces are
   * gone before `applySvgMarkup` ever sees it.
   */
  async function handleSvgUpload(file) {
    if (!isCurrentPicker()) return;
    const name = String(file?.name || '');
    const type = String(file?.type || '').toLowerCase();
    try {
      if (type !== 'image/svg+xml' && !/\.svg$/i.test(name)) {
        throw new Error('That is not an SVG. Choose a .svg file.');
      }
      // 2 MB. An SVG is text; anything past this is a traced raster, which
      // belongs in the image picker and would freeze the canvas here.
      if (file.size > 2 * 1024 * 1024) {
        throw new Error('That SVG is too large. Use one under 2 MB.');
      }
      const raw = await file.text();
      const parsed = parseSvg(raw);
      if (!parsed) throw new Error('That file could not be read as an SVG.');
      const markup = serializeSvg(sanitizeSvg(parsed));
      if (!markup) throw new Error('That file could not be read as an SVG.');
      // A synthetic entry so the decorative branch still has a name to record
      // and the queue still has something to report.
      await applySvgMarkup(markup, { name: name.replace(/\.svg$/i, '') || 'Uploaded SVG' }, null);
    } catch (err) {
      reportError('SVG library upload', err, {
        user: 'toast',
        message: err?.userMessage || err?.message || 'Could not use that SVG file.'
      });
    }
  }

  /**
   * One pick has landed. Fill its slot, advance, and stay open only while the
   * queue has somewhere left to go.
   *
   * @returns {boolean} true if the dialog should STAY OPEN. False is the
   *   unconditional-close behaviour every pre-DS-7 caller had.
   */
  function advanceQueueOrClose(slot) {
    const queue = api?.queue;
    if (!queue || queue.size < 2) return false;
    queue.markFilled(slot, '');
    if (queue.isDrained()) return false;
    // More to fill. The grid stays exactly where it is — the search that found
    // this shape probably finds the next one too — and only the bar moves on.
    queue.advance();
    api.paintQueue();
    // Per-pick, the live region carries the run. G8: N picks are N history
    // entries and N undo toasts, because each applies immediately so the canvas
    // keeps up. Do NOT describe the run as one undo.
    setStatus(`${slot?.label || 'Shape'} updated. ${queue.remainingLabel()}`);
    return true;
  }

  // --- Tabs ----------------------------------------------------------------

  function switchTab(tab) {
    activeTab = tab;
    // `silent` — a programmatic switch must not re-enter through onChange. The
    // control paints itself; only a real click or arrow key calls back.
    chrome?.segmented?.set(tab, { silent: true });
    // The chip row's contents are a function of the source (Pixabay has no
    // categories), so it repaints with the switch. `setChip('all')` also drops
    // a category the new source cannot honour.
    activeCategory = 'all';
    api?.setChip?.('all');
    api?.repaintChips?.();
    // A tab switch abandons whatever the other tab had in flight.
    if (svgPixabayAbort) {
      svgPixabayAbort.abort();
      svgPixabayAbort = null;
    }
    svgPixabayGeneration++;
    removeLoadMore();
    setStatus('');

    const searchInput = api.searchInput;
    if (tab === 'library') {
      hideSvgKeyPrompt();
      if (searchInput) searchInput.placeholder = 'Search SVGs...';
      setLibraryRecords(api.getQuery());
    } else {
      pixabayResults = [];
      api.setRecords([]);
      // The band's credit line follows the source, and there is nothing to
      // count until a search runs.
      paintFoot('');
      if (searchInput) searchInput.placeholder = 'Search Pixabay vectors...';
      if (pixabayVectors.needsKey && !pixabayVectors.getApiKey()) {
        showSvgKeyPrompt();
      } else {
        hideSvgKeyPrompt();
        setStatus('Type at least 2 characters to search.');
        const query = api.getQuery();
        if (query.length >= 2) doPixabaySearch(query, 1);
      }
    }

    if (searchInput?.disabled) chrome.keySettings.focus();
    else api.focusSearch();
  }

  // --- Pixabay -------------------------------------------------------------

  function removeLoadMore() {
    if (pixabayLoadMoreBtn) {
      pixabayLoadMoreBtn.remove();
      pixabayLoadMoreBtn = null;
    }
  }

  function paintLoadMore() {
    removeLoadMore();
    const loaded = pixabayResults.length;
    if (loaded >= pixabayTotal) return;
    pixabayLoadMoreBtn = createButton({
      variant: 'bare',
      className: 'pb-image-picker-load-more',
      label: `Load More (${loaded} of ${pixabayTotal})`,
      onClick: () => {
        if (pixabayLoadMoreBtn?.disabled) return;
        doPixabaySearch(api.getQuery(), pixabayPage + 1);
      }
    });
    // AFTER the scroller, not after the list: see the onOpen comment.
    const scroller = api.listEl.closest('.pb-coll-scroll');
    (scroller || api.listEl).after(pixabayLoadMoreBtn);
  }

  function doPixabaySearch(query, page) {
    if (svgPixabayAbort) {
      svgPixabayAbort.abort();
      svgPixabayAbort = null;
    }
    if (!query || query.trim().length < 2) {
      pixabayResults = [];
      api.setRecords([]);
      removeLoadMore();
      setStatus('Type at least 2 characters to search.');
      return;
    }

    const generation = ++svgPixabayGeneration;
    const isNew = page === 1;
    pixabayPage = isNew ? 1 : page;
    if (isNew) {
      pixabayResults = [];
      removeLoadMore();
    }
    if (pixabayLoadMoreBtn) pixabayLoadMoreBtn.disabled = true;

    const controller = new AbortController();
    svgPixabayAbort = controller;
    setStatus('Searching...');

    pixabayVectors
      .search(query, pixabayPage, controller.signal)
      .then((data) => {
        // The generation guard, not the abort signal: a resolved-but-superseded
        // response must not paint over a newer one.
        if (generation !== svgPixabayGeneration || api.isClosed()) return;
        svgPixabayAbort = null;
        pixabayTotal = data.total;
        const rows = (data.results || []).map((item) => ({ ...item, __pixabay: true }));
        pixabayResults = isNew ? rows : [...pixabayResults, ...rows];
        overflowCount = 0;
        api.setRecords(pixabayResults);
        // DS-8: a SUCCESSFUL search used to clear the status and say nothing —
        // the failure paths all speak and the one that works was silent, which
        // is the wrong way round. `setStatus('')` still clears the visible line
        // (a result count is not chrome); the announcement carries the number.
        if (pixabayResults.length === 0) {
          paintFoot('');
          setStatus('No vectors match your search.');
        } else {
          setStatus('');
          const line = `${pixabayResults.length} vector${pixabayResults.length === 1 ? '' : 's'}`;
          paintFoot(line);
          announce(line);
        }
        paintLoadMore();
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        if (generation !== svgPixabayGeneration || api.isClosed()) return;
        svgPixabayAbort = null;
        if (pixabayLoadMoreBtn) pixabayLoadMoreBtn.disabled = false;

        if (err.code === 'AUTH') {
          setStatus('Invalid API key. Update it in Project settings.');
          showSvgKeyPrompt();
          chrome.keySettings.focus();
        } else if (err.code === 'RATE') {
          setStatus('Rate limited. Try again in a moment.');
        } else if (err.code === 'NO_KEY') {
          setStatus('');
          showSvgKeyPrompt();
          chrome.keySettings.focus();
        } else {
          setStatus('Could not reach Pixabay. Check your connection.');
        }
      });
  }

  /**
   * Handle a Pixabay vector selection — MIME-aware routing.
   * SVG → the caller's callback in callback mode, else
   * replaceSvg()/swapImageToSvg. Raster → image pipeline for current mode.
   *
   * CALLBACK MODE IS THE FIRST BRANCH, and it has to be: this used to route
   * every Pixabay pick to replaceSvg/swapImageToSvg, which act on
   * `activeSvgEdit`/`activeImageEdit`. A callback-mode caller (the logo panel's
   * "SVG Library" button, through `replaceLogoFromSvgLibrary`) has neither, so
   * the click either did nothing at all or — with a stale active edit from an
   * earlier selection — mutated an element the user was not looking at.
   */
  async function handlePixabayPick(item, swap) {
    if (!isCurrentPicker()) return;
    const file = await pixabayVectors.download(item);
    if (!isCurrentPicker()) return;
    const meta = { source: 'Pixabay', author: item.author, authorUrl: item.authorUrl };
    const toCaller = typeof onPick === 'function';

    if (file.type === 'image/svg+xml') {
      // Verify content is actually SVG
      const text = await file.text();
      if (!isCurrentPicker()) return;
      const trimmed = text.trimStart().toLowerCase();
      if (trimmed.startsWith('<?xml') || trimmed.startsWith('<svg')) {
        if (toCaller) {
          // The library tab hands over the index entry; Pixabay's record names
          // its label `title`, so give the caller the `name` it reads either way.
          await onPick(text, { ...item, name: item.name || item.title || '' });
          if (api?.overlay !== svgModal) return;
          markSvgModalPicked();
          closeSvgModal();
        } else if (swap) {
          swapImageToSvg(text);
        } else {
          replaceSvg(text);
        }
        return;
      }
    }

    // A callback-mode caller asked for SVG MARKUP, and the image pipeline below
    // writes through the active edit it does not have. Fail loudly and leave the
    // browser open rather than applying the pick somewhere else.
    if (toCaller) {
      throw Object.assign(new Error('Pixabay returned a raster in callback mode'), {
        userMessage: 'That Pixabay result is a bitmap, not an SVG. Pick a vector result instead.'
      });
    }

    // Raster fallback — route to image pipeline
    if (swap) {
      // IMG→SVG mode picked a raster: replace image with image.
      if ((await applyImageFile(file, meta, capturedOwner.target)) && api?.overlay === svgModal) {
        markSvgModalPicked();
        closeSvgModal();
      }
    } else {
      // SVG replace mode picked a raster: swap SVG to <img>.
      if ((await applySvgToImageFile(file, meta)) && api?.overlay === svgModal) {
        markSvgModalPicked();
        closeSvgModal();
      }
    }
  }
}

/**
 * The close path, shared by every dismissal route. `fromModule` is the
 * browser's own onClose (backdrop, Escape, ×, programmatic); anything else is
 * this file closing the browser, which then calls back in here.
 *
 * FOCUS RESTORE IS THIS FILE'S, not the kit's: the library is reachable from
 * six entry points and has to return focus to whichever control invoked it,
 * which is why the browser is opened with `restoreFocus: false`.
 */
function finalizeSvgModalClose(kit = svgModalKit, { fromModule = false } = {}) {
  if (!fromModule && kit && svgModalKit && kit !== svgModalKit) return;
  const restoreFocus = svgModalRestoreFocusOnClose;
  if (svgPixabayAbort) {
    svgPixabayAbort.abort();
    svgPixabayAbort = null;
  }
  svgPixabayGeneration++;
  svgModal = null;
  svgModalKit = null;
  svgModalRestoreFocusOnClose = true;
  settleSvgModalOutcome();
  if (restoreFocus && svgModalTrigger?.isConnected && typeof svgModalTrigger.focus === 'function') {
    svgModalTrigger.focus();
  }
  svgModalTrigger = null;
}

function closeSvgModal({ restoreFocus = true, invalidatePending = true } = {}) {
  if (invalidatePending) svgModalRequestToken++;
  if (svgModalKit) {
    svgModalRestoreFocusOnClose = restoreFocus;
    svgModalKit.close();
    return;
  }
  svgModalRestoreFocusOnClose = restoreFocus;
  finalizeSvgModalClose();
}

// --- HTML mutation helpers ---

function updateImageSourceAttrs(img, srcUri, assetId = '') {
  img.src = srcUri;
  img.removeAttribute('srcset');
  img.removeAttribute('sizes');
  img.removeAttribute('data-src');
  img.removeAttribute('data-srcset');
  if (!img.hasAttribute('alt')) img.setAttribute('alt', '');
  if (assetId) img.setAttribute('data-pb-asset', assetId);
  else img.removeAttribute('data-pb-asset');
}

function shouldUpdatePictureSource(source, scope) {
  const media = source.getAttribute('media') || '';
  if (scope === PICTURE_SCOPE_ALL) return true;
  if (scope === PICTURE_SCOPE_MOBILE) return mediaLooksMobile(media);
  if (scope === PICTURE_SCOPE_DESKTOP) return mediaLooksDesktop(media);
  return true;
}

function updatePictureSources(picture, srcUri, mimeType, scope) {
  for (const source of picture.querySelectorAll('source')) {
    if (!shouldUpdatePictureSource(source, scope)) continue;
    source.setAttribute('srcset', srcUri);
    source.removeAttribute('sizes');
    source.removeAttribute('data-srcset');
    const media = source.getAttribute('media') || '';
    if (media) {
      source.removeAttribute('type');
    } else if (mimeType) {
      source.setAttribute('type', mimeType);
    } else {
      source.removeAttribute('type');
    }
  }
}

function updateImageInHTML(sectionHtml, targetIdentity, srcUri, altText = '', options = {}) {
  const {
    assetId = '',
    mimeType = '',
    pictureReplaceScope = PICTURE_SCOPE_ALL,
    attribution = null
  } = options;
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${sectionHtml}</div>`, 'text/html');
  const root = doc.body.firstElementChild;

  injectEditableAttrs(root);

  const allEditables = root.querySelectorAll('[data-pb-editable]');
  const editableIndex =
    typeof targetIdentity === 'number' ? targetIdentity : targetIdentity?.editableIndex;
  const eid =
    typeof targetIdentity === 'object'
      ? targetIdentity?.targetRef?.eid || targetIdentity?.eid || ''
      : '';
  const target = eid
    ? root.querySelector(`[data-pb-eid="${escapeForAttrSelector(eid)}"]`)
    : typeof editableIndex === 'number'
      ? allEditables[editableIndex]
      : null;
  if (!target) return null;
  if (target.tagName !== 'IMG') return null;

  const scope = normalizePictureReplaceScope(pictureReplaceScope);
  const picture = target.closest('picture');
  if (picture) {
    if (scope !== PICTURE_SCOPE_MOBILE) {
      updateImageSourceAttrs(target, srcUri, assetId);
    }
    updatePictureSources(picture, srcUri, mimeType, scope);
  } else {
    updateImageSourceAttrs(target, srcUri, assetId);
  }
  // Preserve human-authored alt (including alt=""). If the old image had no
  // alt, create an empty one so the UI nudges the user instead of storing a filename.
  if (!target.hasAttribute('alt')) target.setAttribute('alt', altText || '');
  applyAdjacentImageCredit(doc, target, attribution, assetId);

  stripTempAttrs(root);
  return root.innerHTML;
}

function updateSvgInHTML(sectionHtml, editableIndex, newSvgMarkup, options = {}) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${sectionHtml}</div>`, 'text/html');
  const root = doc.body.firstElementChild;

  injectEditableAttrs(root);

  const allEditables = root.querySelectorAll('[data-pb-editable]');
  if (editableIndex >= allEditables.length) return null;

  const target = allEditables[editableIndex];
  if (target.tagName !== 'svg' && target.tagName !== 'SVG') return null;

  // Sanitize the incoming SVG (library/Pixabay picks are attacker-controllable):
  // strip <script>, on* handlers, and javascript: hrefs before it reaches state.
  const newSvg = parseSvg(newSvgMarkup);
  if (!newSvg) return null;
  sanitizeSvg(newSvg);

  const imported = doc.importNode(newSvg, true);

  if (!imported.getAttribute('width') && target.getAttribute('width')) {
    imported.setAttribute('width', target.getAttribute('width'));
  }
  if (!imported.getAttribute('height') && target.getAttribute('height')) {
    imported.setAttribute('height', target.getAttribute('height'));
  }
  if (
    (options.preserveTargetClass || !imported.getAttribute('class')) &&
    target.getAttribute('class')
  ) {
    imported.setAttribute('class', target.getAttribute('class'));
  }
  if (
    (options.preserveTargetStyle || !imported.getAttribute('style')) &&
    target.getAttribute('style')
  ) {
    imported.setAttribute('style', target.getAttribute('style'));
  }
  if (!imported.getAttribute('data-pb-eid') && target.getAttribute('data-pb-eid')) {
    imported.setAttribute('data-pb-eid', target.getAttribute('data-pb-eid'));
  }
  if (options.decorativeName) {
    imported.setAttribute('data-pb-decorative-name', options.decorativeName);
  }

  target.parentNode.replaceChild(imported, target);

  stripTempAttrs(root);
  return root.innerHTML;
}

/**
 * Swap an editable element for a different type (IMG→SVG or SVG→IMG).
 * When target IMG is inside <picture>, replaces the entire <picture> wrapper.
 * Preserves class, style, data-* attrs from the old element, and width/height for SVG→IMG.
 */
function swapEditableInHTML(sectionHtml, editableIndex, newMarkup, expectedTag, options = {}) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${sectionHtml}</div>`, 'text/html');
  const root = doc.body.firstElementChild;

  injectEditableAttrs(root);

  const allEditables = root.querySelectorAll('[data-pb-editable]');
  if (editableIndex >= allEditables.length) return null;

  const target = allEditables[editableIndex];
  const tag = target.tagName.toUpperCase();
  if (expectedTag === 'IMG' && tag !== 'IMG') return null;
  if (expectedTag === 'SVG' && tag !== 'SVG') return null;

  // Parse new element. Swapping an IMG→SVG inlines attacker-controllable SVG
  // (library/Pixabay), so sanitize it (strip <script>, on*, javascript: hrefs)
  // before it reaches the DOM/state. The SVG→IMG branch parses builder-generated
  // <img> markup (trusted), so it needs no SVG sanitization.
  let newEl;
  if (expectedTag === 'IMG') {
    newEl = parseSvg(newMarkup);
    if (!newEl) return null;
    sanitizeSvg(newEl);
  } else {
    const newDoc = new DOMParser().parseFromString(`<div>${newMarkup}</div>`, 'text/html');
    newEl = newDoc.body.firstElementChild?.firstElementChild;
    if (!newEl) return null;
  }

  // Determine the actual element being replaced (may be <picture> wrapper)
  const picture = expectedTag === 'IMG' ? target.closest('picture') : null;
  const replaceTarget = picture || target;

  // Preserve class, style, and data-* attrs from the replaced element. When an
  // IMG lives inside a picture, the picture is replaced but the IMG owns the
  // useful builder identity/class metadata, so use both as attribute sources.
  const preserveSources = replaceTarget === target ? [replaceTarget] : [replaceTarget, target];
  for (const source of preserveSources) {
    for (const attr of ['class', 'style']) {
      const val = source.getAttribute(attr);
      const shouldForceClass = attr === 'class' && options.preserveTargetClass;
      const shouldForceStyle = attr === 'style' && options.preserveTargetStyle;
      if (val && (shouldForceClass || shouldForceStyle || !newEl.getAttribute(attr))) {
        newEl.setAttribute(attr, val);
      }
    }
    for (const attr of source.attributes) {
      if (
        attr.name.startsWith('data-') &&
        attr.name !== 'data-pb-editable' &&
        !newEl.hasAttribute(attr.name)
      ) {
        newEl.setAttribute(attr.name, attr.value);
      }
    }
  }
  if (options.decorativeName) {
    newEl.setAttribute('data-pb-decorative-name', options.decorativeName);
  }

  // Preserve the source element's width/height on the swapped-in element so the
  // box is unchanged (avoids layout shift), unless the new markup sets its own.
  // SVG→IMG carries the old <svg>'s dims; IMG→SVG carries the source <img>'s dims.
  {
    const w = target.getAttribute('width');
    const h = target.getAttribute('height');
    if (w && !newEl.getAttribute('width')) newEl.setAttribute('width', w);
    if (h && !newEl.getAttribute('height')) newEl.setAttribute('height', h);
  }

  const imported = doc.importNode(newEl, true);
  replaceTarget.parentNode.replaceChild(imported, replaceTarget);

  stripTempAttrs(root);
  return root.innerHTML;
}

/**
 * @deprecated Use replaceImage() which opens the unified picker.
 * Kept as compatibility wrapper for stale imports.
 */
export function replaceImageFromPexels() {
  return replaceImage();
}

/**
 * @deprecated Use swapSvgToImage() which opens the unified picker.
 * Kept as compatibility wrapper for stale imports.
 */
export function swapSvgToStockPhoto() {
  return swapSvgToImage();
}

export function getActiveImageEdit() {
  return activeImageEdit;
}

export function getActiveSvgEdit() {
  return activeSvgEdit;
}

// Test-only exports: real async commit paths and HTML transforms, with external costs
// controlled by the media repair and media-integrity suites.
export {
  onImageFileDropped as __onImageFileDroppedForTests,
  applyImageFile as __applyImageFileForTests,
  applySvgToImageFile as __applySvgToImageFileForTests,
  updateImageInHTML as __updateImageInHTMLForTests,
  updateSvgInHTML as __updateSvgInHTMLForTests,
  swapEditableInHTML as __swapEditableInHTMLForTests,
  sanitizeSvgMarkupForInline as __sanitizeSvgMarkupForInlineForTests
};

// --- Explicit-target wrappers for the decorative context menu ---------------
// These do NOT mutate activeImageEdit / activeSvgEdit. They thread the target
// through closures so the modal pick callback fires on the right element even
// if a real selection happens before the user picks.

export function replaceImageForTarget(target) {
  if (!target) return Promise.resolve();
  return replaceImage(target);
}

export function replaceSvgForTarget(svgMarkup, target, options = {}) {
  if (!target) return;
  const validity = validateReplacementTarget(target, { mimeType: 'image/svg+xml' });
  if (!validity.ok) {
    eventBus.emit('editing:error', { message: `Replacement skipped: ${validity.reason}` });
    return false;
  }
  const resolved = resolveSectionByInstance(target.instanceId);
  if (!resolved) return false;

  const { closeModal: shouldCloseModal = true, ...mutationOptions } = options;
  const currentEditable = findEditableByIndex(resolved.section.html, target.editableIndex);
  const oldEid = target.eid || currentEditable?.getAttribute('data-pb-eid') || '';
  const currentTag = (currentEditable?.tagName || target.tagName || '').toUpperCase();
  let updatedHtml = null;
  if (target.kind === 'decoration') {
    const decorativeIndex = listDecorativeSvgs(resolved.section.html).findIndex(
      (entry) => entry.eid === oldEid
    );
    if (decorativeIndex >= 0) {
      updatedHtml = replaceDecorativeSvg(resolved.section.html, decorativeIndex, svgMarkup, {
        name: mutationOptions.decorativeName || '',
        filename: mutationOptions.decorativeFilename || ''
      });
    }
  } else {
    updatedHtml =
      currentTag === 'IMG'
        ? swapEditableInHTML(
            resolved.section.html,
            target.editableIndex,
            svgMarkup,
            'IMG',
            mutationOptions
          )
        : updateSvgInHTML(resolved.section.html, target.editableIndex, svgMarkup, mutationOptions);
  }
  if (updatedHtml === null) {
    eventBus.emit('editing:error', { message: 'Could not replace decorative SVG' });
    return false;
  }

  store.update(resolved.path, updatedHtml, 'Replace decorative SVG');
  if (!replaceEditableLiveFromHtml(target.instanceId, updatedHtml, oldEid)) {
    eventBus.emit('preview:render-request', { preserveScroll: true });
  }

  if (shouldCloseModal) {
    markSvgModalPicked();
    closeSvgModal();
  }
  eventBus.emit('editing:svg-replaced', { kind: target.kind || 'decoration' });
  return true;
}

export async function openDecorativeSvgLibraryForTarget(target, { triggerEl = null } = {}) {
  const captured = snapshotReplacementTarget(target);
  if (!captured.ok) return;
  target = captured.target;
  const focusEl =
    triggerEl || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
  const requestToken = ++svgModalRequestToken;
  showSvgModal([], false, { loading: true, triggerEl: focusEl, library: 'decorative' });

  try {
    const svgs = await loadSvgIndex('decorative');
    if (requestToken !== svgModalRequestToken || !svgModal) return;
    if (!validateReplacementTarget(target).ok) {
      closeSvgModal();
      return;
    }
    showSvgModal(svgs, false, {
      triggerEl: focusEl,
      library: 'decorative',
      onPick: (markup, entry) =>
        replaceSvgForTarget(markup, target, {
          preserveTargetClass: true,
          preserveTargetStyle: true,
          decorativeName: entry?.name || '',
          decorativeFilename: entry?.filename || '',
          closeModal: false
        })
    });
    beginSvgModalOutcome();
  } catch (err) {
    if (requestToken === svgModalRequestToken) {
      closeSvgModal({ restoreFocus: false, invalidatePending: false });
    }
    console.error('[ImageEditor] Failed to load decorative SVG library for target:', err);
    eventBus.emit('editing:error', { message: 'Failed to load decorative SVG library' });
  }
}

export function removeDecorativeForTarget(target) {
  if (!target) return;
  const resolved = resolveSectionByInstance(target.instanceId);
  if (!resolved) return;

  let updatedHtml = null;
  if (target.eid) {
    updatedHtml = removeDecorativeByEid(resolved.section.html, target.eid);
  }
  if (updatedHtml === null && Number.isInteger(target.decorativeIndex)) {
    updatedHtml = removeDecorativeSvg(resolved.section.html, target.decorativeIndex);
  }
  if (updatedHtml === null) return;

  store.update(resolved.path, updatedHtml, 'Remove decorative');
  if (!replaceEditableLiveFromHtml(target.instanceId, updatedHtml, target.eid || '')) {
    eventBus.emit('preview:render-request', { preserveScroll: true });
  }
  eventBus.emit('editing:decorative-removed', {});
}

export function removeBackgroundImageForTarget(target) {
  if (!target) return;
  const resolved = resolveSectionByInstance(target.instanceId);
  if (!resolved) return;

  // CSS-background path: replace url(...) with `none` via the dedicated clear
  // helper (the replace API rejects empty/blank URLs).
  if (target.editableType === 'css-background') {
    if (!target.targetRef && !Number.isInteger(target.cssBgIndex)) return;
    const updatedCss = clearSectionCssBackgroundImage(
      resolved.section.css,
      resolved.section.sectionId,
      target.targetRef || target.cssBgIndex
    );
    if (updatedCss === null) return;
    store.update(`${resolved.sectionPath}.css`, updatedCss, 'Remove background image');
    eventBus.emit('preview:render-request', { preserveScroll: true });
    eventBus.emit('editing:image-removed', { instanceId: target.instanceId });
    return;
  }

  // Picture-background path: walk the picture, clear srcset on every <source>
  // and src/srcset on the <img>, keep the <picture> element in place so users
  // can re-add an image afterward.
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${resolved.section.html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return;

  let picture = null;
  if (target.eid) {
    const escaped = String(target.eid).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const eidEl = root.querySelector(`[data-pb-eid="${escaped}"]`);
    if (eidEl) {
      picture = eidEl.tagName.toLowerCase() === 'picture' ? eidEl : eidEl.closest('picture');
    }
  }
  if (!picture && Number.isInteger(target.editableIndex)) {
    injectEditableAttrs(root);
    const editables = Array.from(root.querySelectorAll('[data-pb-editable]'));
    const el = editables[target.editableIndex];
    if (el) {
      picture = el.tagName.toLowerCase() === 'picture' ? el : el.closest('picture');
    }
  }
  if (!picture) return;

  Array.from(picture.querySelectorAll('source')).forEach((source) => {
    source.removeAttribute('srcset');
  });
  const img = picture.querySelector('img');
  if (img) {
    img.removeAttribute('src');
    img.removeAttribute('srcset');
    img.removeAttribute('data-pb-asset');
    img.removeAttribute('data-pb-mime');
  }

  stripTempAttrs(root);
  const updatedHtml = root.innerHTML;

  store.update(resolved.path, updatedHtml, 'Remove background image');
  const oldEid = target.eid || '';
  if (!replaceEditableLiveFromHtml(target.instanceId, updatedHtml, oldEid)) {
    eventBus.emit('preview:render-request', { preserveScroll: true });
  }
  eventBus.emit('editing:image-removed', { instanceId: target.instanceId });
}

// --- Image info & mutation helpers for the image editor panel ---

/**
 * Extract image info (src, alt, link state) from section HTML by editable index.
 * Returns { src, alt, hasLink, linkHref, className, widthAttr, heightAttr } or null if not found.
 */
export function getImageInfoFromHTML(sectionHtml, editableIndex) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${sectionHtml}</div>`, 'text/html');
  const root = doc.body.firstElementChild;

  injectEditableAttrs(root);

  const allEditables = root.querySelectorAll('[data-pb-editable]');
  if (editableIndex >= allEditables.length) return null;

  const target = allEditables[editableIndex];
  if (target.tagName !== 'IMG') return null;

  const wrapTarget = target.closest('picture') || target;
  const parentLink = wrapTarget.parentElement?.closest('a');

  return {
    src: target.getAttribute('src') || '',
    alt: target.getAttribute('alt') || '',
    title: target.getAttribute('title') || '',
    className: target.getAttribute('class') || '',
    widthAttr: target.getAttribute('width') || '',
    heightAttr: target.getAttribute('height') || '',
    tintColor: target.getAttribute('data-tint-color') || '',
    hasLink: !!parentLink,
    linkHref: parentLink ? parentLink.getAttribute('href') || '' : ''
  };
}

/**
 * Update the alt attribute on an image editable. Returns new section HTML or null.
 */
export function updateAltInHTML(sectionHtml, editableIndex, alt) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${sectionHtml}</div>`, 'text/html');
  const root = doc.body.firstElementChild;

  injectEditableAttrs(root);

  const allEditables = root.querySelectorAll('[data-pb-editable]');
  if (editableIndex >= allEditables.length) return null;

  const target = allEditables[editableIndex];
  if (target.tagName !== 'IMG') return null;

  target.setAttribute('alt', alt);

  stripTempAttrs(root);
  return root.innerHTML;
}

/**
 * Update the title attribute on an image editable. Returns new section HTML or null.
 */
export function updateTitleInHTML(sectionHtml, editableIndex, title) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${sectionHtml}</div>`, 'text/html');
  const root = doc.body.firstElementChild;

  injectEditableAttrs(root);

  const allEditables = root.querySelectorAll('[data-pb-editable]');
  if (editableIndex >= allEditables.length) return null;

  const target = allEditables[editableIndex];
  if (target.tagName !== 'IMG') return null;

  if (title) {
    target.setAttribute('title', title);
  } else {
    target.removeAttribute('title');
  }

  stripTempAttrs(root);
  return root.innerHTML;
}

/**
 * Update the data-tint-color attribute on an image editable.
 * Pass empty string or null to remove. Returns new section HTML or null.
 */
export function updateTintColorInHTML(sectionHtml, editableIndex, tintColor) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${sectionHtml}</div>`, 'text/html');
  const root = doc.body.firstElementChild;

  injectEditableAttrs(root);

  const allEditables = root.querySelectorAll('[data-pb-editable]');
  if (editableIndex >= allEditables.length) return null;

  const target = allEditables[editableIndex];
  if (target.tagName !== 'IMG') return null;

  if (tintColor) {
    target.setAttribute('data-tint-color', tintColor);
  } else {
    target.removeAttribute('data-tint-color');
  }

  stripTempAttrs(root);
  return root.innerHTML;
}

/**
 * Wrap an image (or its <picture> parent) in an <a> tag.
 * If already wrapped in a link, updates the href.
 * Returns new section HTML or null.
 */
export function wrapImageInLink(sectionHtml, editableIndex, href) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${sectionHtml}</div>`, 'text/html');
  const root = doc.body.firstElementChild;

  injectEditableAttrs(root);

  const allEditables = root.querySelectorAll('[data-pb-editable]');
  if (editableIndex >= allEditables.length) return null;

  const target = allEditables[editableIndex];
  if (target.tagName !== 'IMG') return null;

  const wrapTarget = target.closest('picture') || target;
  const existingLink = wrapTarget.parentElement?.closest('a');

  if (existingLink) {
    existingLink.setAttribute('href', href);
  } else {
    const anchor = doc.createElement('a');
    anchor.setAttribute('href', href);
    wrapTarget.parentNode.insertBefore(anchor, wrapTarget);
    anchor.appendChild(wrapTarget);
  }

  stripTempAttrs(root);
  return root.innerHTML;
}

/**
 * Remove the <a> wrapper from an image (or its <picture> parent).
 * Returns new section HTML or null. Returns unchanged HTML if no link wrapper exists.
 */
export function unwrapImageLink(sectionHtml, editableIndex) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${sectionHtml}</div>`, 'text/html');
  const root = doc.body.firstElementChild;

  injectEditableAttrs(root);

  const allEditables = root.querySelectorAll('[data-pb-editable]');
  if (editableIndex >= allEditables.length) return null;

  const target = allEditables[editableIndex];
  if (target.tagName !== 'IMG') return null;

  const wrapTarget = target.closest('picture') || target;
  const link = wrapTarget.parentElement?.closest('a');

  if (link) {
    link.parentNode.insertBefore(wrapTarget, link);
    link.remove();
  }

  stripTempAttrs(root);
  return root.innerHTML;
}
